import { getCustomersLoyaltyCheck } from '@agos/api-client/customers'
import { getGeocodeReverse } from '@agos/api-client/geo'
import { useGetSalesChannelsPublic } from '@agos/api-client/orders'
import { apiErrorField, customFetch, retryTransient } from '@agos/api-client'
import {
  useGetCampaignsActivePromo,
  useGetCampaignsHappyHoursPublic,
} from '@agos/api-client/marketing'
import {
  usePostCouponsValidate,
  useGetBurgerBoxConfig,
  useGetCategories,
  useGetGiftsProductRulesPublic,
  useGetProducts,
} from '@agos/api-client/menu'
import type { CreateOrderRequest } from '@agos/api-client/model'
import {
  useGetDeliveryZones,
  useGetDeliveryZonesDepot,
  usePostOrders,
} from '@agos/api-client/orders'
import { usePostPaymentsInitiate } from '@agos/api-client/payments'
// Tags follow the backend's controllers, not this app's modules: the referral check and the loyalty
// settings are the customers controller's, and the payment settings are the payments controller's.
import {
  useGetCustomerAuthMe,
  useGetCustomerAuthMeSavedAddresses,
  useGetLoyaltyPublicInfo,
  usePostCustomersRegister,
  usePostOrdersValidateReferral,
} from '@agos/api-client/customers'
import { useGetSettingsPaymentsPublic } from '@agos/api-client/payments'
import { useGetSettingsLunchPublic, useGetSettingsOpeningHours } from '@agos/api-client/platform'
import {
  T,
  asksUpsellFor,
  centsToEuros,
  formatCents,
  formatCount,
  boxFixedPricesOf,
  offerUpsell,
  toDateParam,
  formatNamedDayTime,
  geocodeOnce,
  tt,
  zoneOutlinesOf,
  type UpsellPrompt,
} from '@agos/shared'
import {
  AlertTriangle,
  Info,
  ArrowLeft,
  Banknote,
  CalendarClock,
  Clock,
  CheckCircle2,
  CreditCard,
  Loader2,
  Mail,
  MapPin,
  Minus,
  Package,
  Soup,
  Phone,
  LocateFixed,
  Plus,
  QrCode,
  StickyNote,
  Store,
  UtensilsCrossed,
  Trash2,
  Truck,
  User,
} from 'lucide-react'
import { cn,
  AddonPrompt,
  AddressField,
  toast,
} from '@agos/ui'
import { useQuery } from '@tanstack/react-query'

import {
  courierDeliveryFor,
  courierPaymentSettings,
  courierQuoteOf,
  DELIVERY_QUOTE_PATH,
  quotePath,
} from './courier-delivery'
import { useEffect, useMemo, useRef, useState, type InputHTMLAttributes } from 'react'
import { Link, Redirect, useLocation } from 'wouter'

import {
  addressSummary,
  announcementOf,
  announcementShows,
  rememberOrder,
  AMOUNT_GIFT_RULES_KEY,
  amountGiftOffers,
  amountGiftRulesOf,
  amountGiftsToRemove,
  productGiftsToRemove,
  fetchAmountGiftRules,
  giftLine,
  giftOffers,
  giftRulesOf,
  depositCentsOf,
  lunchLineOf,
  upsellLinesFor,
  type PricedLine,
} from '@agos/customer-core'
import { groupBasket } from './basket-groups'
import {
  addressesOf,
  defaultAddress,
  allLunchMeals,
  balanceNote,
  currentLevelKey,
  composedSubtotalCents,
  lunchMealsIn,
  lunchOrderLines,
  lunchSettingsOf,
  lunchWeeksOf,
  lunchSubtotalCents,
  maxRedeemable,
  multiplierNote,
  quickAmounts,
  type QuickAmount,
  tierFor,
  pointsAreBlocked,
  pointsValueCents,
  lineKey,
  priceLines,
  ratesOf,
  redemptionNote,
  subtotalCents,
  useMinute,
  type PricedProduct,
} from '@agos/customer-core'
import {
  NO_DISCOUNTS,
  payableCents,
  replacedNote,
  discountBreakdown,
  personalDiscountCents,
  personalDiscountLabel,
  replacesEverything,
  roundingNote,
  totalDiscountCents,
} from '@agos/customer-core'
import { mayOrder, openNote, openState, type OpeningHours } from '@agos/customer-core'
import { useCart } from '@agos/customer-core'
import { useCustomerAuth } from '@agos/customer-core'
import {
  couponCents,
  couponFrom,
  couponRefusal,
  referralDiscountCents,
  stillApplies,
  organizationNote,
  type AppliedCoupon,
} from './coupon'
import {
  addressToResolve,
  basketProgress,
  deliveryFor,
  deliveryNote,
  progressFraction,
  zoneLine,
  zonesOf,
} from './delivery'
import { CheckoutPinMap } from './CheckoutPinMap'
import {
  foundForAddress,
  givenAsAPlace,
  heldWhenAnAddressIsChosen,
  pointBehind,
  type HeldPoint,
} from './delivery-pin'
import { orderPayload, submitProblems, type CheckoutFacts } from './order-payload'
import { submitBlock } from './submit-block'
import {
  paymentMethodLabel,
  paymentMethodNote,
  paymentMethodsFor,
  paymentSettingsOf,
  paymentSettingsUnknown,
  paymentSubstitutionNote,
  paymentUnknownNote,
  selectedPaymentMethod,
  substitutedPaymentMethod,
  type PaymentMethod,
} from './payment-methods'
import {
  activePromoOf,
  blackFridayCents,
  blackFridayLabel,
  freeDeliveryApplies,
  happyHoursCents,
  happyHoursLabel,
  happyHoursOf,
  happyHoursPointsNote,
} from './promo'
import {
  lunchDayText,
  lunchPreorderDate,
  lunchPreorderEnforced,
  lunchServingWindow,
  lunchSlots,
  scheduledAtOf,
  schedulingProblem,
  type Preorder,
} from './preorder'
import { chosenType, orderTypesOf } from './order-types'
import { paymentErrorPath } from './payment-error'
import { chargeCents, identityDiscountsApply } from './payment-amount'

/**
 * The checkout.
 *
 * Every rule on this screen lives in a tested module beside it — `coupon`, `delivery`, `preorder`,
 * `discounts`, `order-payload` — and this file only wires them to inputs. That is deliberate: legacy's
 * `Checkout.tsx` is 2 700 lines with the arithmetic inline, which is how five discount sources ended up
 * with two rounding rules (FB-C04) and nobody noticed.
 *
 * **The total is the server's formula**, in the server's order:
 * `payable(max(0, subtotal + delivery − discount + deposits))`. The five-cent step lands once, on the
 * total; every discount keeps its cent.
 *
 * The deposit is zero and will stay zero until a backend change: no public route exposes the
 * product-to-surcharge join, so the app cannot know a product carries one. That is FB-C05's client half,
 * recorded in `BLOCKED.md` rather than counted as done.
 */

/** A key per checkout attempt, so a double tap answers the first order instead of placing a second. */
function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/**
 * A labelled field, as legacy writes them: an icon, the label, and a required marker.
 *
 * This screen had bare placeholders — which vanish the moment somebody types, so a half-filled form
 * stops saying what its boxes are, and a screen reader is told nothing at all. FB-C53.
 */
const FIELD_CLASS =
  'w-full px-4 py-3 rounded-xl bg-background border border-border transition-colors outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary'

function CheckoutField(props: {
  id: string
  icon: typeof User
  label: string
  hint?: string
  children: React.ReactNode
}) {
  const Icon = props.icon
  return (
    <div className="space-y-2">
      <label htmlFor={props.id} className="text-sm font-semibold flex items-center gap-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
        {/* The marker is **inside** the label legacy passes - `<T>Meno a priezvisko *</T>` - not a span
            beside it. Rendering it separately put a gap before the asterisk and coloured it red, and
            `slovak-parity.test.ts` missed the string difference by 0.006: 17 characters of 19 is 0.894
            against a 0.9 threshold. FB-C63. */}
        {props.label}
      </label>
      {props.children}
      {props.hint !== undefined && <p className="text-xs text-muted-foreground">{props.hint}</p>}
    </div>
  )
}

export function CheckoutPage() {
  const [, navigate] = useLocation()
  const lines = useCart((state) => state.lines)
  const add = useCart((state) => state.add)
  const clear = useCart((state) => state.clear)
  const changeBy = useCart((state) => state.changeBy)
  const removeLine = useCart((state) => state.remove)
  // §9.166 — the checkout's first caller. The cart has exported it since the box was built.
  const removeBoxGroup = useCart((state) => state.removeBoxGroup)
  // §9.712, #136 - the basket's *+* is an added unit, so it is asked about like the menu's (`asksUpsellFor`).
  const [addonPrompt, setAddonPrompt] = useState<UpsellPrompt | null>(null)
  const stepLine = (entry: PricedLine, delta: number) => {
    changeBy(entry.key, delta)
    if (delta <= 0 || !asksUpsellFor(entry.line)) return
    void offerUpsell({ productId: entry.line.productId, productName: entry.name, parentKey: entry.key }, setAddonPrompt)
  }
  const user = useCustomerAuth((state) => state.user)

  const { data: productData, isLoading: productsLoading, isSuccess: productsRead } = useGetProducts({})
  // §9.142 — what the basket earns right now. Same rule and same helper the menu uses; legacy
  // duplicates it into both of its pages, which is how two copies drift.
  /* FB-831, §9.546 - **an offer nobody sees cannot be recovered downstream.** `giftOffers` decides what the basket
     **offers**, and the server takes `giftRuleId` on a line the client sends - so a gift the customer was never shown
     cannot be added later. FB-804 measured 60 % of qualifying web baskets taking one, on a live rule of
     `minCount: 1`. That is the one of this screen's three discount reads that loses money; the other two are the
     server's own arithmetic (see `promoUnreadable` below). */
  const {
    data: giftRuleData,
    isError: giftsFailed,
    isSuccess: giftsRead,
    refetch: refetchGifts,
  } = useGetGiftsProductRulesPublic()
  // §9.697, owner #21 / O75 - *Darčeky podľa sumy*, read without a login (§10A.353). The menu reads the same key.
  const amountGiftRead = useQuery({ queryKey: AMOUNT_GIFT_RULES_KEY, queryFn: fetchAmountGiftRules })
  const { data: categoryData, isSuccess: categoriesRead } = useGetCategories()
  /* FB-830, §9.545 - **three refusals are computed from this read**, and all three become unreachable when its
     answer is empty: `deliveryFor` opens on `zones.length === 0`, so no `below-minimum` and no `outside` - while
     `maySubmit` explicitly allows `none-configured`. Their own rows carry 31 zones at Šurany and 8 at Nesvady, so
     that branch was only ever reachable here by a failed read. */
  const { data: zoneData, isError: zonesFailed, refetch: refetchZones } = useGetDeliveryZones()
  // §10A.121 - the depot, read rather than derived. A restaurant moves about never, so this is
  // cached for the session: the point only decides which of two overlapping zones wins.
  const { data: depot } = useGetDeliveryZonesDepot({ query: { staleTime: 30 * 60_000 } })
  // Legacy re-reads this every 60 seconds (§14.1) — a campaign can start while a basket is open.
  const { data: promoData, isError: promoFailed } = useGetCampaignsActivePromo({
    query: { refetchInterval: 60_000 },
  })
  // §9.254 — an hour ends while somebody is filling in the form, so this is re-read on the same
  // interval the opening hours are. The server decides; this is what the summary shows.
  const { data: happyData, isError: happyFailed } = useGetCampaignsHappyHoursPublic({
    query: { refetchInterval: 60_000 },
  })
  /* FB-831, §9.546 - **these two quote too high rather than overcharging**, and that is measured rather than assumed:
     `WebOrderDiscounts` computes happy hours and black Friday **server-side** from the campaign rows, independently of
     what the client sends. So a failed read makes the screen show a total **without** a discount the server will
     apply - a deterrent, not money taken. One sentence between them, because from the customer's side it is one
     fact. */
  const promoUnreadable = promoFailed || happyFailed
  const {
    data: hoursData,
    isLoading: hoursLoading,
    isError: hoursError,
    // Legacy re-reads the hours every minute here as well as on the menu (§14.1). A checkout open across
    // closing time must find out from the server, not from the copy it loaded twenty minutes ago.
  } = useGetSettingsOpeningHours({ query: { refetchInterval: 60_000 } })
  const { data: lunchData, isLoading: lunchLoading } = useGetSettingsLunchPublic()
  /**
   * §10A.107 - the box configuration, for its fixed price.
   *
   * **The checkout has to read this even though the menu screen already did.** This is the screen a
   * customer reads a total off before they pay, and `ServerPricing` will apply the fixed price
   * whether or not this page knows about it. Without this the basket would show the sum of the parts
   * and the bill would say something else, which is §10A.80's defect from the customer's side.
   */
  const { data: boxData } = useGetBurgerBoxConfig()
  /* §9.501 - `isError` as well as the data, because *card is switched off* and *we could not find out* are
   * different facts and the checkout could not tell them apart. `isError` was already destructured for the
   * opening-hours read ten lines above; this query simply never used it. */
  const {
    data: paymentsData,
    isError: paymentsFailed,
    refetch: refetchPayments,
  } = useGetSettingsPaymentsPublic()
  // `/loyalty/public-info`, **never** `/settings/loyalty`. The C0 spec records the decision and the
  // reason is FB-138: the settings endpoint answers 44 fields including a live mail credential, and
  // *public* in an endpoint name is a claim worth verifying rather than trusting. The profile already
  // reads the right one; this page briefly read the wrong one.
  const { data: loyaltyData } = useGetLoyaltyPublicInfo()
  /* Only a signed-in customer has points to spend; the query is idle otherwise.

     FB-835, §9.549 - **`retry: false` here meant a blip cost the customer the points they had earned.** This read
     carries the balance, so one dropped packet on the screen where they are spending it leaves them unable to; and
     `retry: false` was never about the 401, which `enabled` already prevents. `retryTransient` keeps a 401 final and
     gives a 500 or a dropped connection a second go. */
  const account = useGetCustomerAuthMe({ query: { enabled: user !== null, retry: retryTransient } })
  const validateReferral = usePostOrdersValidateReferral()
  const joinProgramme = usePostCustomersRegister()
  /* FB-835, §9.549 - the same, and what it cost was a returning customer retyping their address. */
  const savedAddresses = useGetCustomerAuthMeSavedAddresses({
    query: { enabled: user !== null, retry: retryTransient },
  })

  const validateCoupon = usePostCouponsValidate()
  // One key per mount, so a double tap answers the first order rather than placing a second. Generated
  // here rather than per submit: a key that changes on retry is not an idempotency key.
  const [idempotencyKey] = useState(newIdempotencyKey)
  const placeOrder = usePostOrders({
    request: { headers: { 'X-Idempotency-Key': idempotencyKey } },
  })
  const initiatePayment = usePostPaymentsInitiate()

  /**
   * §9.50 — the channels the franchise offers on the web, not a hard-coded pair.
   *
   * `type` is a slug now, and every branch below asks the chosen channel what it *does*. That is
   * §9.5's rule reaching the customer site: a franchise that adds *Terasa* and marks it for the web
   * gets it here without a release.
   */
  const { data: channelData } = useGetSalesChannelsPublic()
  const orderTypes = useMemo(() => orderTypesOf(channelData), [channelData])
  const [type, setType] = useState<string>('delivery')
  const channel = chosenType(orderTypes, type)
  const needsAddress = channel.requiresDeliveryAddress
  /* §9.786, owner O139 - whether external couriers deliver here, in which case delivery is priced by distance rather
     than by the zones. One read with no address, cached: it says the mode and nothing else for a restaurant that
     keeps its own drivers. Unanswered, the zones stay - the server prices the order either way. */
  const modeQuote = useQuery({
    queryKey: ['delivery-quote'],
    queryFn: () => customFetch<unknown>(DELIVERY_QUOTE_PATH),
    staleTime: 5 * 60_000,
    retry: retryTransient,
  })
  const couriersDeliver = courierQuoteOf(modeQuote.data)?.external === true
  /* §9.802, owner O149 - the zones under the map's pin, to look at only: *"so just they can see their in doručovacia
     zóna"*. None where external couriers deliver, because there no zone prices anything. */
  const zoneOutlines = useMemo(() => zoneOutlinesOf(zoneData), [zoneData])
  const pinMapZones = couriersDeliver ? [] : zoneOutlines
  // **Typed-or-known, rather than filled-in-an-effect.** Legacy prefills all four of these from the
  // session; this port filled only the e-mail, so a signed-in customer retyped their name, phone and
  // address on every order — and `defaultAddress`, written to find the one they marked, was called by
  // nobody (FB-C58).
  //
  // `null` means *not touched*. The alternative — writing the profile into state once it arrives — is
  // the field that resets under a cursor mid-typing, and it is what the shell's setState-in-effect rule
  // exists to prevent. Deriving it needs no effect and cannot race the request.
  const [typedName, setName] = useState<string | null>(null)
  const [typedPhone, setPhone] = useState<string | null>(null)
  const [typedEmail, setEmail] = useState<string | null>(null)
  const [typedAddress, setAddress] = useState<string | null>(null)
  // §9.706, FB-1010 - the point **and what it belongs to**. The order's point is `pointBehind(heldPoint, ...)`,
  // derived below once the address is known, so a typed address stops carrying the last suggestion's point.
  const [heldPoint, setHeldPoint] = useState<HeldPoint | null>(null)
  /**
   * The text the address field last handed over - what a picked suggestion's point belongs to when the provider
   * gave no formatted address (`AddressField` then leaves the suggestion's own name in the box). Written in the
   * field's handler and read in the next one, never during render.
   */
  const fieldText = useRef('')
  useMinute()

  const [chosenPayment, setChosenPayment] = useState<PaymentMethod | null>(null)
  const [redeem, setRedeem] = useState(0)
  // §9.71 — one box for both kinds of code.
  const [codeInput, setCodeInput] = useState('')
  const [codeError, setCodeError] = useState('')
  const [referral, setReferral] = useState<string | null>(null)
  const [referralProblem, setReferralProblem] = useState('')
  const [referralCents, setReferralCents] = useState(0)
  const [joinLoyalty, setJoinLoyalty] = useState(false)
  const [notes, setNotes] = useState('')
  /**
   * This tab is on its way somewhere - §9.422, FB-749.
   *
   * **The owner:** *"when we are using cardpay online in customer website, we are redirected from
   * checkout to home page and then to tatrapay."* `clear()` runs the moment the order is placed,
   * which is right - the order exists - but it empties `lines`, and the guard below then renders
   * `<Redirect to="/" />` **while `initiatePayment` is still in flight**. Creating a gateway session
   * is a round trip to AGOS which is itself a round trip to the bank, so the home page had time to
   * paint before `window.location.assign` fired.
   *
   * The guard exists to stop somebody *browsing* to an empty checkout. It has no business firing
   * during a submission, which is the whole of this fix.
   *
   * **Set at the top of `submit`, not beside `clear()`, and that is the whole of the fix.** The
   * first version set it on the line above `clear()` and **failed in the whole suite while passing
   * alone** - the shape of a race rather than a bug
   * (`agos-passes-alone-fails-in-its-file-is-a-missing-wait`). `useCart` is a **zustand** store, so
   * `clear()` notifies its subscribers and React can re-render *outside* the batch holding a
   * `setLeaving(true)` queued microseconds earlier: the guard then renders with an empty basket and
   * `leaving` still `false`, and the customer sees the home page.
   *
   * Setting it before the first `await` puts a whole await boundary between the state and anything
   * that empties the basket, so there is no ordering left to lose.
   *
   * **A `useRef` was the second attempt and lint was right to refuse it** - *Cannot access refs
   * during render*. A ref read during render is exactly the inconsistency this guard must not have.
   */
  const [leaving, setLeaving] = useState(false)
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null)
  const [couponError, setCouponError] = useState('')
  const [preorder, setPreorder] = useState<Preorder | null>(null)
  const [submitError, setSubmitError] = useState('')

  const products = useMemo(
    () => (Array.isArray(productData) ? (productData as PricedProduct[]) : []),
    [productData],
  )
  const zones = useMemo(() => zonesOf(zoneData), [zoneData])

  // §9.142 — the gift the basket has earned and not yet taken. Legacy evaluates the same rule on its
  // checkout as well as its menu; a customer who crossed the threshold and came straight here would
  // otherwise never be told. Recomputed rather than carried over, so it also follows a line removed
  // on the way.
  const countable = useMemo(() => {
    const categories = Array.isArray(categoryData)
      ? (categoryData as { id?: number; name?: string }[])
      : []
    return lines.map((line) => ({
      line,
      categoryName:
        categories.find(
          (category) =>
            category.id ===
            (products.find((product) => product.id === line.productId) as
              | { categoryId?: number }
              | undefined)?.categoryId,
        )?.name ?? '',
    }))
  }, [categoryData, lines, products])
  // §9.167 — every rule the basket has earned. This read `[0]`, so a basket qualifying under two
  // rules was told about one and the second stayed invisible until the first was accepted. Legacy
  // maps the whole list; nothing caught it here because the fixture has one rule.
  const checkoutGifts = useMemo(() => giftOffers(giftRulesOf(giftRuleData), countable), [giftRuleData, countable])
  /* §9.765, owner O126 - a product gift leaves with the items that earned it, here as on the menu. Judged only once the
     rules, the products and the categories have answered, so a slow read never takes a gift away. */
  const productRules = giftsRead && productsRead && categoriesRead ? giftRulesOf(giftRuleData) : null
  const staleProductGifts = productGiftsToRemove(productRules, countable)
    .map((take) => `${take.key}\t${String(take.quantity)}`)
    .join('\n')
  useEffect(() => {
    if (staleProductGifts === '') return
    for (const entry of staleProductGifts.split('\n')) {
      const [key, quantity] = entry.split('\t')
      if (key !== undefined && quantity !== undefined) changeBy(key, -Number(quantity))
    }
  }, [staleProductGifts, changeBy])
  const hours = hoursLoading || hoursError ? null : (hoursData as OpeningHours | null)

  // Which methods the franchise will take, and which one is selected — recomputed rather than stored,
  // because the order type can change *after* a method was picked and a restaurant that does not take
  // cards on pickup would otherwise leave the customer holding one.
  /* §9.786, owner O139 - where external couriers deliver, *Karta - terminál* is not offered on a delivery: the courier
     carries none. Applied to the settings every payment rule below reads, so the tiles, the selection and §9.479's
     substitution notice cannot disagree about it. */
  const payments = useMemo(
    () => courierPaymentSettings(paymentSettingsOf(paymentsData), couriersDeliver && needsAddress),
    [paymentsData, couriersDeliver, needsAddress],
  )
  const methods = paymentMethodsFor(payments, needsAddress)
  const paymentMethod = selectedPaymentMethod(payments, needsAddress, chosenPayment)
  /* §9.479, owner R3 - **the swap was silent and the order went out under the other method.** When the
   * public settings read stops answering, `card_online` leaves `methods`, its tile leaves the screen, cash
   * lights up, and the submit reads `paymentMethod` rather than `chosenPayment`. The customer is told their
   * order is placed and never asked for money, which leaves no `card_online` row for §10A.207's panel. */
  const substitution = substitutedPaymentMethod(payments, needsAddress, chosenPayment)
  /* §9.501 - and whether the fallback above is a fact or a gap. Measured: card online ran at about 27 % of
   * delivery and pickup orders to 19 September and about 11 % after, on flat volume, while card at the door
   * rose - so a sentence asserting *card is unavailable* when the truth is *we could not check* costs a card
   * payment every time somebody believes it. */
  const paymentsUnknown = paymentSettingsUnknown(paymentsData, paymentsFailed)

  // Loyalty points: what this customer has, and how many the programme will let them spend.
  const rates = useMemo(() => ratesOf(loyaltyData), [loyaltyData])
  const profileRow = (
    account.data as
      | { user?: { pointsBalance?: unknown; orderCount?: unknown; loyaltyLevel?: unknown } }
      | undefined
  )?.user
  const balance = typeof profileRow?.pointsBalance === 'number' ? profileRow.pointsBalance : 0
  /**
   * §9.218 — what a signed-in member is greeted with, under the e-mail.
   *
   * All four figures are already on `account.data.user`, which this component reads for the balance
   * two lines above; the tier is resolved from the franchise's own thresholds rather than from a
   * hard-coded five, exactly as the profile page does it.
   */
  const memberOrderCount = typeof profileRow?.orderCount === 'number' ? profileRow.orderCount : 0
  const memberLevel = currentLevelKey(
    rates,
    memberOrderCount,
    typeof profileRow?.loyaltyLevel === 'string' ? profileRow.loyaltyLevel : null,
  )
  // §9.250 — the tier as the franchise writes it, from the reader the profile uses, so the two
  // screens cannot end up with different words for the same tier.
  const memberTier = tierFor(loyaltyData, memberLevel)
  // §9.454 - the sentence depends on what else is on the basket, and `beforePoints` is computed
  // below with the rest of the money. Threaded at the render rather than re-derived here.
  const redeemNoteFor = (hasAnotherDiscount: boolean) =>
    redemptionNote(rates, balance, hasAnotherDiscount)
  // §9.278 — `redeemable` and `redeemedCents` moved below the other discounts, because the ceiling
  // is now the cart's as well as the balance's and the cart is not known this early.

  // The addresses this customer has ordered to before. Legacy offers them here and this port did not,
  // so a signed-in customer retyped their address on every order.
  const saved = useMemo(() => addressesOf(savedAddresses.data), [savedAddresses.data])

  const profile = (account.data as { user?: Record<string, unknown> } | undefined)?.user
  const knownName = typeof profile?.name === 'string' ? profile.name : (user?.name ?? '')
  const knownPhone = typeof profile?.phone === 'string' ? profile.phone : ''
  const knownEmail = typeof profile?.email === 'string' ? profile.email : (user?.email ?? '')
  // The address they marked as theirs. Legacy pre-trusts it — it came from an order that arrived.
  const knownAddress = defaultAddress(saved)?.address ?? ''

  const customerName = typedName ?? knownName
  const customerPhone = typedPhone ?? knownPhone
  const customerEmail = typedEmail ?? knownEmail
  const customerAddress = typedAddress ?? knownAddress
  /* §9.809, owner O154 - points and the trvalá zľava are keyed on the e-mail, and the server honours them only while
     the order's e-mail is the account's. `discountUser` is the account for the money below, or nobody once another
     e-mail is typed, so the preview drops what the server will drop. */
  const identityHolds = user !== null && identityDiscountsApply(knownEmail, customerEmail)
  const discountUser = identityHolds ? user : null
  /**
   * §9.706, FB-1010 - the point the order carries: priced by `deliveryFor`, sent as `deliveryLat`/`deliveryLon`
   * and drawn as the pin. A suggestion's point while the address still begins with the text it was found for, a
   * pin or *Moja poloha* while nothing else is chosen - never the last point whatever was typed after it.
   */
  const point = pointBehind(heldPoint, customerAddress)
  /* §9.786, owner O139 - the fee for this address where external couriers deliver: the courier's reward for the road
     distance, which the server quotes and charges by the same rule. Asked only in that mode and only for a delivery
     with a point; the mode itself is one cached read above. */
  const pointQuote = useQuery({
    queryKey: ['delivery-quote', point?.lat ?? null, point?.lng ?? null],
    queryFn: () => customFetch<unknown>(quotePath(point)),
    enabled: couriersDeliver && needsAddress && point !== null,
    retry: retryTransient,
  })

  const fixedBoxPrices = useMemo(() => boxFixedPricesOf(boxData), [boxData])
  const priced = priceLines(lines, products, fixedBoxPrices)

  // The daily menu is a second catalogue: `/products` does not carry the day's meals, and their price
  // depends on what else is in the basket (a soup is cheaper beside a main). Both halves are totalled
  // and both are sent — the split into two lines for a partly-bundled soup happens here, once, from
  // the same rule the menu screen showed.
  const lunchMeals = useMemo(() => allLunchMeals(lunchData), [lunchData])
  /**
   * §9.219 — the day this basket's daily-menu lines are for, and the hours that menu is served.
   *
   * Read off the basket rather than remembered from the menu screen: a meal is added from a day and
   * then somebody walks away, adds a pizza and comes back.
   */
  const lunchDay = useMemo(
    // `toDateParam` rather than a fourth copy of the same three lines — two modules already carry
    // their own `localDate` and this is the shared one.
    () => lunchPreorderDate(lines, lunchWeeksOf(lunchData), toDateParam(new Date())),
    [lines, lunchData],
  )
  const lunchWindow = useMemo(() => lunchServingWindow(lunchSettingsOf(lunchData)), [lunchData])
  const lunchSlotOptions = useMemo(() => lunchSlots(lunchWindow), [lunchWindow])
  const lunchBundlePrice = useMemo(
    () => lunchSettingsOf(lunchData).soupBundlePrice ?? null,
    [lunchData],
  )
  const lunchBasket = useMemo(() => lunchMealsIn(lines, lunchMeals), [lines, lunchMeals])
  const lunchCents = lunchSubtotalCents(lunchBasket, lunchBundlePrice)

  // A kombo pizza and anything else the customer assembled: no catalogue to price it from, so the price
  // it was composed at travels on the line itself.
  const composedLines = useMemo(() => lines.filter((line) => line.composed !== undefined), [lines])
  const composedCents = composedSubtotalCents(lines)
  // The lunch half is priced by its own rule, so it joins the subtotal rather than the catalogue.
  const subtotal = subtotalCents(priced) + lunchCents + composedCents
  const couponLines = priced.map((entry) => ({
    productId: entry.line.productId,
    quantity: entry.line.quantity,
    unitCents: entry.unitCents,
  }))

  // A product coupon whose item has left the basket is not applied, and is not shown as if it were.
  const liveCoupon = stillApplies(coupon, couponLines) ? coupon : null
  /**
   * §10A.121 - the address and the depot decide an overlap, and the server uses both.
   *
   * The depot is **read from the server** rather than geocoded here: the tie-break falls back to
   * depot distance when no zone name appears in the address, and a client computing that from its
   * own geocode would quote a fee the customer is not charged. `GET /delivery-zones/depot` exists so
   * both sides read the same two numbers. A depot that cannot be resolved answers nulls, which both
   * sides treat identically - fall back to the smallest zone.
   */
  const depotPoint =
    typeof depot?.lat === 'number' && typeof depot.lng === 'number'
      ? { lat: depot.lat, lng: depot.lng }
      : null
  const delivery = couriersDeliver
    ? courierDeliveryFor(
        needsAddress ? point : null,
        courierQuoteOf(pointQuote.data),
        subtotal,
        pointQuote.isError,
      )
    : deliveryFor(needsAddress ? point : null, zones, subtotal, {
        zonesUnreadable: zonesFailed,
        address: customerAddress,
        depot: depotPoint,
      })
  const rawDeliveryCents = delivery.kind === 'ok' ? delivery.feeCents : 0

  const exclusive = replacesEverything(discountUser, subtotal)

  // The two campaigns that change what an order costs. The **server** decides them — `OrderCampaigns`
  // evaluates both at order creation, written that way because legacy applied Black Friday in the
  // browser alone and stored the undiscounted total. Nothing here is sent; this is the summary agreeing
  // with what will be charged, rather than the checkout quoting a higher number than the bill (FB-C44).
  const promo = useMemo(() => activePromoOf(promoData), [promoData])
  const codeApplied = liveCoupon !== null || referral !== null
  const promoCents = exclusive ? 0 : blackFridayCents(promo, subtotal, codeApplied)
  // §9.254 — a personal discount that replaces everything replaces this too, as it does the
  // other four; happy hours must not be the one that leaks past §9.65's rule.
  const happy = happyHoursOf(happyData)
  const happyCents = exclusive ? 0 : happyHoursCents(happy, subtotal)
  const deliveryFree = freeDeliveryApplies(promo, subtotal, needsAddress)
  const deliveryCents = deliveryFree ? 0 : rawDeliveryCents

  /* §9.278, owner O23 — what the order can still absorb in points.
   *
   * **Every other discount is subtracted and the points are not**, which is the half legacy's own
   * comment warns about: a ceiling computed from a total that already has the points taken off
   * shrinks on every drag and the slider fights the person using it. */
  const beforePoints = exclusive
    ? 0
    : couponCents(liveCoupon, couponLines, subtotal) +
      (referral === null ? 0 : referralCents) +
      promoCents +
      happyCents
  /* §9.809, owner O154 - **a stackable trvalá zľava is another discount, as `WebOrderDiscounts` counts it.** Left out
     of `beforePoints`, the switch below offered points beside it that the server then dropped, and the payment was
     refused as *Suma platby nezodpovedá objednávke*. With the switch on, the ceiling is what it leaves as well. */
  const personalBesidePoints = exclusive ? 0 : personalDiscountCents(discountUser, subtotal)
  const absorbableCents = Math.max(0, subtotal + rawDeliveryCents - beforePoints - personalBesidePoints)
  /* §9.454, BACKEND.md §10A.217 - *Kombinovať vernostné body so zľavami*, off.
   *
   * **`beforePoints > 0` is the test, and it is already the right expression.** It is the sum of
   * every other discount on this basket - coupon, referral, Black Friday, happy hours - computed
   * just above for the slider's ceiling, which is exactly the question the server asks of its own
   * running total. Naming the coupon alone would have allowed points beside a referral.
   *
   * **Modelled on `exclusive`, deliberately.** A personal rate that replaces everything has zeroed
   * the points since §9.254, and this is the same shape for the same reason: the server will refuse
   * them, so a slider that offers them quotes a price the server will not honour - and §9.278's own
   * comment is that a preview which disagrees with the server is worse than no preview. */
  const pointsBlocked = pointsAreBlocked(rates, beforePoints + personalBesidePoints)
  const redeemable =
    exclusive || pointsBlocked || !identityHolds ? 0 : maxRedeemable(rates, balance, absorbableCents)
  const redeemedCents = pointsValueCents(rates, Math.min(redeem, redeemable))

  const discount = totalDiscountCents(
    discountUser,
    {
      ...NO_DISCOUNTS,
      // Every source goes through one place, because the server caps the **sum** once and against
      // subtotal plus delivery — adding any of them to the total separately is how a preview drifts.
      couponCents: exclusive ? 0 : couponCents(liveCoupon, couponLines, subtotal),
      // §9.454 - and out of the preview's total as well, or the screen quotes a price the server
      // will not honour. `redeemable` is already 0, so this is belt and braces on one line.
      pointsCents: exclusive || pointsBlocked ? 0 : redeemedCents,
      referralCents: exclusive || referral === null ? 0 : referralCents,
      blackFridayCents: promoCents,
      happyHoursCents: happyCents,
    },
    { subtotalCents: subtotal, deliveryCents },
  )
  /* §9.697, owner #21 / O75 - *"nad 70€ kofola zdarma"*. Decided (#124): reached by **the food after discounts,
     without delivery**, which is the subtotal less every discount this page has just added up - the same figure the
     server checks (§10A.354). `null` while the rules have not answered, so nothing is offered or taken on a blip. */
  const amountRules = amountGiftRead.isSuccess ? amountGiftRulesOf(amountGiftRead.data) : null
  const foodCents = Math.max(0, subtotal - discount)
  const amountGifts = amountRules === null ? [] : amountGiftOffers(amountRules, foodCents, lines)
  // Below the amount the gift goes - *"if the order drops below, the gift is removed"* - here as well as on the
  // menu, because a coupon typed on this page is what usually takes an order under it. **Keyed on a string**, so the
  // effect runs when the set of stale gifts changes rather than on every render: a removal that found nothing would
  // otherwise write the basket, re-render and try again for ever.
  const staleAmountGifts = amountGiftsToRemove(amountRules, foodCents, lines)
    .map((line) => lineKey(line))
    .join('\n')
  useEffect(() => {
    if (staleAmountGifts === '') return
    for (const key of staleAmountGifts.split('\n')) removeLine(key)
  }, [staleAmountGifts, removeLine])

  // §9.182 — the deposits the server will add. This passed a literal `0` while
  // `OrderCreationService.surchargeTotal` charged them, so a basket with a deposit was quoted 2,15 €
  // and billed 2,30 €.
  const deposits = depositCentsOf(priced, products)
  const exact = Math.max(0, subtotal + deliveryCents + deposits - discount)
  const total = payableCents({
    subtotalCents: subtotal,
    deliveryCents,
    depositCents: deposits,
    discountCents: discount,
  })

  const state = openState(hours, new Date())
  // §9.243 — the owner's notice reaches the checkout too. A customer holding a basket who is
  // being told they cannot order should not have to go back to the menu to read why.
  const announcement = announcementOf(hoursData)
  // A guest, and a programme that is actually running. Read twice — the offer itself and the reason the
  // e-mail field gives for wanting an address.
  const joinLoyaltyOffered = user === null && rates !== null && rates.isActive

  /**
   * §9.163, the owner's point 47 — whether the typed address already has an account.
   *
   * Debounced, because an address arrives a character at a time and this is one question, not twelve.
   * Only while the programme is on offer: a signed-in visitor is already that person, and a franchise
   * with loyalty off has nothing to check.
   */
  const [existingAccount, setExistingAccount] = useState(false)
  const [isLocating, setIsLocating] = useState(false)

  /**
   * §9.164 — legacy's *Moja poloha*, which is three steps and not one.
   *
   * The old version set `point` and stopped, so the address box stayed empty and the order button
   * stayed disabled: the button looked broken because the part that fills the field had never been
   * written. Legacy takes the browser's coordinates to the server, writes `display_name` into the
   * field, and keeps the point — which is what makes an address Google has never heard of usable.
   *
   * Every failure says which one it was. Setting `point` to null on refusal, as this did before, is
   * indistinguishable on screen from nothing having happened at all.
   */
  const locate = () => {
    if (typeof navigator.geolocation === 'undefined') {
      toast({
        variant: 'destructive',
        title: tt('Geolokácia nedostupná'),
        description: tt('Váš prehliadač nepodporuje určenie polohy.'),
      })
      return
    }
    setIsLocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        // The point is kept whatever the lookup says: the zone is decided by coordinates, and a
        // provider that cannot name the spot has not made the delivery impossible. §9.706: the customer's own
        // place, so correcting the address the lookup writes below keeps it.
        setHeldPoint(givenAsAPlace({ lat: latitude, lng: longitude }))
        void getGeocodeReverse({ lat: String(latitude), lon: String(longitude) })
          .then((place) => {
            // `ReverseGeocode` always answers — the coordinates become `display_name` when nothing
            // can name the point, so there is no empty-string branch to guard here.
            setAddress(place.display_name)
            toast({
              title: tt('Poloha zistená'),
              description: tt('Adresa bola vyplnená podľa aktuálnej polohy.'),
            })
          })
          .catch(() => {
            toast({
              variant: 'destructive',
              title: tt('Chyba'),
              description: tt('Nepodarilo sa získať adresu z polohy.'),
            })
          })
          .finally(() => setIsLocating(false))
      },
      () => {
        setIsLocating(false)
        toast({
          variant: 'destructive',
          title: tt('Prístup zamietnutý'),
          description: tt('Povoľte prístup k polohe v prehliadači.'),
        })
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }
  /**
   * The preselected default address, resolved - §9.371, FB-659.
   *
   * **`customerAddress` is `typedAddress ?? knownAddress`**, so a returning customer arrives with
   * `defaultAddress(saved)` already in the field and **no `point`** behind it - and `zoneFor` needs
   * the point, so the fee could not be quoted and the checkout asked for a zone. The owner's report:
   * *"when I go checkout in customer and I have preselected or select some, it is not actually
   * verified, and asking for Doručovacia zóna."*
   *
   * §9.52 fixed the **click** on a saved chip - *"chosen means resolved"* - and this is the same
   * defect one path over, on the commonest case of all.
   *
   * **Each guard is a bug it would otherwise be.** Never when a point exists: a resolved point is
   * better than a fresh lookup and overwriting it would move a quote somebody already saw. Never
   * once they have typed: from then on the field owns the address and its own `onResolved` answers
   * for it. Never on a blank. And a failed lookup leaves the point alone, which is §9.52's rule.
   */
  useEffect(() => {
    const address = addressToResolve({ point, typedAddress, knownAddress })
    if (address === null) return
    let cancelled = false
    void geocodeOnce(address).then((place) => {
      if (!cancelled && place !== null) {
        setHeldPoint(foundForAddress({ lat: place.lat, lng: place.lon }, address))
      }
    })
    return () => {
      cancelled = true
    }
  }, [knownAddress, typedAddress, point])

  useEffect(() => {
    // No synchronous `setState` here: the reset is *derived* at render instead. Setting state on the
    // way out of an effect is a cascading render, and the linter is right to refuse it.
    const address = customerEmail.trim()
    if (!joinLoyaltyOffered || !address.includes('@')) return
    let cancelled = false
    const timer = setTimeout(() => {
      void getCustomersLoyaltyCheck({ email: address })
        .then((answer) => {
          // **One field, and that is the whole answer.** BLOCKED #11 / §10A.8: this endpoint used to
          // hand a stranger the owner's name, order count, tier and balance for any address they
          // could guess. It says `found` now and nothing else, so this panel can say an account
          // exists and cannot say whose — which is the correct amount for somebody who has proved
          // only that they can type.
          if (!cancelled) setExistingAccount((answer as { found?: boolean }).found === true)
        })
        .catch(() => {
          if (!cancelled) setExistingAccount(false)
        })
    }, 500)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [customerEmail, joinLoyaltyOffered])
  // Shut, and no pre-order to sidestep it with. Legacy's `orderingBlocked`, by another name.
  /**
   * §9.709 (owner O92, decision #131) - a future day's lunch locks the časenka onto that day.
   *
   * §9.219 applied it as *a default and not a lock*: once per day, and a switch-off afterwards stuck,
   * so the menu went out as an immediate order for today. Now the časenka is put back on the lunch day
   * whenever anything has taken it off it, and a basket with no future lunch day is left alone - which
   * is also the release when the meals leave the basket. Set during render rather than in an effect, as
   * §9.219's default was: `lunchPreorderEnforced` answers the same object once it is satisfied, so this
   * settles in one pass.
   */
  const enforcedPreorder = lunchPreorderEnforced(preorder, lunchDay)
  if (enforcedPreorder !== preorder) setPreorder(enforcedPreorder)
  const lunchLocked = lunchDay !== null

  const closed = !mayOrder(state) && preorder === null
  /* §9.709 - the lunch day's own refusals first (off, another date, no time, outside the serving
     window), then the opening hours. One sentence, read by `maySubmit` and said by `submitBlock`. */
  const scheduling = schedulingProblem({
    preorder,
    lunchDay,
    lunchWindow,
    hours,
    now: new Date(),
  })
  /**
   * The chosen slot, in the words the manager's časenka already uses — §9.18.
   *
   * `null` until both halves are set, because *sobota 2. 08. 00:00* from a half-filled form reads as a
   * decision somebody made rather than one they have not made yet.
   */
  const scheduledSummary = useMemo(() => {
    if (preorder === null || preorder.date === '' || preorder.time === '') return null
    const scheduledAt = scheduledAtOf(preorder.date, preorder.time)
    return scheduledAt === null ? null : formatNamedDayTime(scheduledAt)
  }, [preorder])
  const facts: CheckoutFacts = {
    // §9.474 - the capability the page has used in six places all along, now also reaching the payload
    // and the validator, which both asked the slug.
    requiresAddress: needsAddress,
    type,
    customerName,
    customerPhone,
    customerEmail,
    customerAddress,
    point,
    paymentMethod,
    notes,
    deliveryCents,
    couponCode: liveCoupon?.code ?? null,
    referralCode: referral,
    redeemedPoints: Math.min(redeem, redeemable),
    preorder,
  }
  /* §9.418 - the whole basket, not just the half the catalogue can price.
   *
   * `lunchBasket` and `composedLines` are the same two terms `emptyBasket` and `resolvesToNothing`
   * use fourteen lines apart at the guard below. `priced` omits both - a daily meal is not in
   * `/products` and a kombo prices itself - so passing `priced` alone called a filled basket empty
   * and refused an order the server would have taken. */
  const problems = submitProblems(facts, priced, lunchBasket.length + composedLines.length)

  // A preorder is allowed while the restaurant is shut — that is what it is for. An immediate order is
  // not, and `openState` answering `unknown` counts as not (FB-C06).
  const maySubmit =
    problems.length === 0 &&
    scheduling === '' &&
    (preorder !== null || mayOrder(state)) &&
    delivery.kind !== 'below-minimum' &&
    /* FB-830, §9.545 - `unreadable` is allowed **deliberately**: blocking would refuse a customer who is inside a
       zone and above the minimum on a network blip, and the server is the authority either way. What the customer
       gets instead is the sentence and a retry, which is what turns a possible late refusal into an avoidable one. */
    (!needsAddress ||
      delivery.kind === 'ok' ||
      delivery.kind === 'none-configured' ||
      delivery.kind === 'unreadable') &&
    !placeOrder.isPending

  /* §9.417 - the same terms, read back as a sentence to put beside the button.
   *
   * **The owner had to infer a cause from two amounts** (FB-745): *Záväzne objednať za 7,90 €* did
   * nothing, 10,90 € worked, and the report that followed guessed at a zone minimum on a channel
   * that has none. The refusal was right; it was simply unreadable. `problems` had been computed
   * directly above here and read by `maySubmit` alone since this page was written - six reasons a
   * customer could act on, none of which had ever reached the screen.
   *
   * It sits next to `maySubmit` on purpose: these two must not drift, and `submit-block.test.ts`
   * asserts they cannot by recomputing this predicate over every combination of its inputs. */
  const blocked = submitBlock({
    problems,
    scheduling,
    delivery,
    needsAddress,
    subtotalCents: subtotal,
  })

  /** Answers whether the code was accepted, so §9.71's single box can fall through to the referral. */
  async function applyCoupon(typed: string): Promise<boolean> {
    const code = typed.trim().toUpperCase()
    if (code === '') return false
    setCouponError('')
    try {
      const answer = await validateCoupon.mutateAsync({
        data: {
          code,
          orderAmount: subtotal / 100,
          customerEmail: customerEmail.trim() === '' ? undefined : customerEmail.trim(),
          cartProductIds: couponLines.map((entry) => entry.productId),
        },
      })
      const applied = couponFrom(answer)
      if (applied === null) {
        setCouponError(couponRefusal(answer) ?? tt('Kupón sa nepodarilo uplatniť.'))
        return false
      }
      setCoupon(applied)
      return true
    } catch (failure) {
      // The server's own wording, which is written for the customer.
      const body = (failure as { body?: unknown }).body
      setCouponError(couponRefusal(body) ?? tt('Kupón sa nepodarilo uplatniť.'))
      return false
    }
  }

  /**
   * One box, both codes — §9.71, owner decision 2026-08-06.
   *
   * Legacy has a single field and AGOS split it, which is the difference the owner reported. The
   * split existed for a real reason: the two endpoints refuse for different reasons, and collapsing
   * them to *neplatný kód* throws away the only useful half of a refusal.
   *
   * So the code is tried as a coupon, then as a referral, and **a refusal from both says both were
   * tried**. That is the sentence legacy's single box cannot produce.
   *
   * Coupon first because it is the common case, and because its endpoint answers the discount the
   * preview must not invent. A referral code reaching it first costs one refused validation.
   */
  async function applyCode() {
    const typed = codeInput.trim()
    if (typed === '') return
    setCodeError('')

    const couponWorked = await applyCoupon(typed.toUpperCase())
    if (couponWorked) {
      setCodeInput('')
      return
    }
    const referralWorked = await applyReferral(typed)
    if (referralWorked) {
      setCodeInput('')
      return
    }
    setCodeError(
      `${tt('Kód')} ${typed} ${tt('neplatí ako zľavový kupón ani ako odporúčací kód.')}`,
    )
  }

  /**
   * Check a referral code before the order carries it.
   *
   * Checked here rather than at submit because a code that will be refused should be refused while
   * there is still a form to correct it on — legacy validates at submit and the customer finds out by
   * having their order rejected.
   *
   * The **e-mail goes with the code**: the server refuses a customer referring themselves, and it needs
   * to know who is asking to do that.
   */
  async function applyReferral(typed: string): Promise<boolean> {
    setReferralProblem('')
    const code = typed.trim()
    if (code === '') return false
    try {
      // The server answers the discount it will apply; the preview must not invent one.
      const answer = (await validateReferral.mutateAsync({
        data: { code, email: customerEmail.trim() },
      })) as { discountType?: unknown; discountValue?: unknown } | null
      setReferralCents(referralDiscountCents(answer, subtotal))
      setReferral(code)
      return true
    } catch (failure) {
      setReferralProblem(apiErrorField(failure, 'error') ?? tt('Kód sa nepodarilo overiť.'))
      return false
    }
  }

  async function submit() {
    // §9.422 - before the first await, so the flag is flushed long before `clear()` can re-render.
    setLeaving(true)
    setSubmitError('')
    try {
      const order = await placeOrder.mutateAsync({
        data: orderPayload(facts, priced, [
          ...lunchOrderLines(lunchBasket, lunchBundlePrice),
          ...composedLines.map((line) => ({
            productId: line.productId,
            quantity: line.quantity,
            unitPriceCents: line.composed?.unitPriceCents ?? 0,
            customName: line.composed?.name ?? '',
            // A gift travels the same road — it has no catalogue price either — but it must arrive with
            // its rule id, or the server prices it as a product and the customer pays for their gift.
            giftRuleId: line.giftRuleId,
            // §9.697 - so the server reads an amount gift in `gift_rules` rather than in the product rules.
            giftRuleType: line.giftRuleType,
            // §9.722 - what the builder assembled, so the server can bound the line and the till can reopen
            // it. Dropped here until now: every web custom pizza and kombo was stored without its parts.
            ...(line.composed?.parts === undefined ? {} : { parts: line.composed.parts }),
            ...(line.composed?.composition === undefined ? {} : { composition: line.composed.composition }),
          })),
        ]) as unknown as CreateOrderRequest,
      })
      const placed = order as { id?: number; orderNumber?: string; total?: unknown } | null
      // §9.809, owner O154 - the order the server wrote is the one being paid, so its total is the amount.
      const charged = chargeCents(placed, total)
      clear()

      // After the order, and never before it: joining is a favour the customer is doing the restaurant,
      // and a registration that failed must not take an order down with it. Legacy does the same and
      // calls it non-fatal, which is right.
      if (joinLoyalty && customerEmail.trim() !== '') {
        await joinProgramme
          .mutateAsync({
            data: {
              email: customerEmail.trim(),
              name: customerName.trim(),
              phone: customerPhone.trim(),
              isLoyalty: true,
            },
          })
          .catch(() => undefined)
      }

      // The landing paths carry no order reference — `Landing` is two bare paths — so what the success
      // page will be able to say is left here, in this tab, before the browser goes anywhere.
      rememberOrder({
        points: null,
        orderId: placed?.id ?? null,
        orderNumber: placed?.orderNumber ?? null,
        // §9.505 - the amount too, because a retry on the payment-error page calls `initiate` and that
        // needs all three. Written before the session is attempted, like the rest of this note.
        totalCents: charged,
      })

      if (paymentMethod === 'card_online' && placed?.id !== undefined) {
        /* §9.470, owner report 2026-09-25 - **its own `try`, because the order has already been
         * placed.**
         *
         * `initiate` throws in three places and all of them are before it writes the
         * `online_payments` row: the gateway not being configured, the underpayment guard, and the
         * bank refusing the session - which is §10A.166's four outcomes, an **open breaker** among
         * them. Wrapped in the outer `catch` those all reported *Objednávku sa nepodarilo odoslať*,
         * and the order had gone. The owner's panel counted **39** such orders; a customer told their
         * order was never placed very reasonably places it again.
         *
         * So a failed session lands on the page the branch below already names as the right one, and
         * only a failed `POST /orders` says the order was not sent. */
        let session: unknown
        try {
          // The amount is checked against the order's own total server-side, so this is a request
          // rather than an instruction.
          session = await initiatePayment.mutateAsync({
            data: {
              orderId: placed.id,
              orderNumber: placed.orderNumber ?? '',
              amount: centsToEuros(charged),
            },
          })
        } catch (sessionFailure) {
          /* **The bank's own reason is carried rather than replaced.** §10A.166 exists so that an
           * open breaker, a refused token exchange, a refused create and an answer with no URL are
           * four different sentences; folding them into one was throwing that away twice over. */
          navigate(paymentErrorPath(apiErrorField(sessionFailure, 'error')))
          return
        }
        const redirect = (session as { redirectUrl?: string } | null)?.redirectUrl
        if (typeof redirect === 'string' && redirect !== '') {
          window.location.assign(redirect)
          return
        }
        // The order exists and is unpaid, which is exactly what the payment-error page is for. Saying
        // nothing and staying here would leave a placed order looking like a failed submission.
        navigate('/payment-error')
        return
      }
      navigate('/success')
    } catch (failure) {
      // The order did not go, so this tab is staying: release the guard, or a basket emptied
      // afterwards could never send the customer back to the menu.
      setLeaving(false)
      setSubmitError(apiErrorField(failure, 'error') ?? tt('Objednávku sa nepodarilo odoslať.'))
    }
  }

  // An empty basket has nothing to check out, and legacy sends the customer back to the menu rather
  // than showing them a form with nothing in it. Rendering as a redirect rather than firing one in an
  // effect: the effect version paints the empty page for a frame first.
  //
  // **Both halves count.** A basket of nothing but daily menu resolves no catalogue lines at all —
  // lunch meals are not in `/products` — so checking `priced` alone would turn a customer with two
  // soups and a burger straight back around at the door.
  //
  // **And the catalogue has to have answered.** `priced` is the basket resolved against `/products`,
  // which is empty on the first render, so this used to redirect *before the products arrived*: anyone
  // arriving at the checkout without the catalogue already in the query cache — a refresh, a bookmark,
  // a link, a return from the bank — was bounced to the menu with a full basket. The flows never saw
  // it because they walk in from the menu, where the catalogue is already cached. FB-C52.
  const emptyBasket = lines.length === 0 && lunchBasket.length === 0 && composedLines.length === 0
  const resolvesToNothing = priced.length === 0 && lunchBasket.length === 0 && composedLines.length === 0

  // **And the same for the lunch menu, which FB-C52 fixed for the catalogue only.** A basket holding
  // nothing but a daily menu resolves to nothing until `lunchData` arrives, so the guard bounced it
  // exactly as it used to bounce a full one. It never showed while the menu page fetched lunch under
  // the same query key — the checkout always found it warm in the cache. §10A.48 gave that page a
  // `from` parameter, the keys stopped matching, and a lunch-only basket started redirecting on a
  // cold cache: a refresh, a bookmark, or a return from the bank.
  if (!leaving && (emptyBasket || (resolvesToNothing && !productsLoading && !lunchLoading))) {
    return <Redirect to="/" />
  }

  // §9.422 - the order is away and the basket is empty, so **every** guard below would now fire: the
  // redirect above is held off by `leaving`, and then `resolvesToNothing` is true and the customer
  // gets a bare spinner with no page around it. Holding the first guard was only half the fix.
  //
  // **The owner's ask was about what the customer sees between the button and the bank** - *"we are
  // redirected from checkout to home page and then to tatrapay, can you make it that we will stay in
  // checkout before redirecting"*. So this is the checkout, still mounted, still at `/checkout`,
  // saying which of the two things is happening. A spinner that says nothing would satisfy the URL
  // and not the request.
  if (leaving) {
    return (
      <main className="max-w-5xl mx-auto px-4 py-8" data-checkout data-checkout-leaving>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-lg font-bold">
            {paymentMethod === 'card_online' ? (
              <T>Presmerúvame vás na platobnú bránu</T>
            ) : (
              <T>Odosielame vašu objednávku</T>
            )}
          </p>
          <p className="text-sm text-muted-foreground">
            <T>Nezatvárajte prosím túto stránku.</T>
          </p>
        </div>
      </main>
    )
  }

  if (resolvesToNothing) {
    // Lines in hand, catalogue still coming. Say nothing rather than flashing a form with no prices in
    // it; the wait is one request and the alternative is losing the basket.
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-checkout-loading>
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-8 pb-32 overflow-x-hidden" data-checkout>
      <div className="flex items-center gap-4 mb-8">
        <Link
          href="/"
          data-to-menu
          aria-label={tt('Späť na menu')}
          className="p-3 bg-card rounded-full shadow-sm hover:bg-secondary active:bg-secondary/80 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-3xl font-display font-bold">
          <T>Dokončenie objednávky</T>
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
<section className="bg-card rounded-3xl p-6 border border-border shadow-sm space-y-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm">1</span>
          <T>Spôsob doručenia</T>
        </h2>
        <div className="grid grid-cols-2 gap-4" data-order-type>
        {orderTypes.map((option) => {
          // The icon follows the capability, as `iconFor` does in the manager: one that takes an
          // address is a van, one that seats people is a restaurant, anything else is a counter. A
          // per-slug guess would break for the fifth channel exactly as the hard-coded pair did.
          const Icon = option.requiresDeliveryAddress ? Truck : option.usesTables ? UtensilsCrossed : Store
          return (
            <button
              key={option.slug}
              type="button"
              data-order-type-option={option.slug}
              aria-pressed={type === option.slug}
              onClick={() => setType(option.slug)}
              className={`cursor-pointer flex flex-col items-center gap-3 p-4 rounded-2xl border-2 transition-all ${
                type === option.slug
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-border hover:border-primary/30 text-muted-foreground'
              }`}
            >
              <Icon className="w-8 h-8" />
              <span className="font-semibold">
                <T>{option.name}</T>
              </span>
            </button>
          )
        })}
      </div>
      </section>

      <section className="bg-card rounded-3xl p-6 border border-border shadow-sm space-y-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm">2</span>
          <T>Vaše údaje</T>
        </h2>
        <div className="space-y-5" data-customer-fields>
        {/* Name and phone are one row in legacy, and one row is what they read as. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <CheckoutField id="field-name" icon={User} label={tt('Meno a priezvisko *')}>
            <input
              id="field-name"
              data-field="name"
              autoComplete="name"
              value={customerName}
              onChange={(event) => setName(event.target.value)}
              placeholder={tt('Napr. Jozef Mrkvička')}
              className={FIELD_CLASS}
            />
          </CheckoutField>
          <CheckoutField id="field-phone" icon={Phone} label={tt('Telefónne číslo *')}>
            <input
              id="field-phone"
              data-field="phone"
              type="text"
              inputMode="tel"
              autoComplete="tel"
              value={customerPhone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="0912 345 678"
              className={FIELD_CLASS}
            />
          </CheckoutField>
        </div>
        <CheckoutField
          id="field-email"
          icon={Mail}
          // Legacy marks it required only while the programme is on offer, and says *(voliteľné)*
          // otherwise rather than leaving the field unexplained.
          label={joinLoyaltyOffered ? tt('E-mail *') : `${tt('E-mail')} ${tt('(voliteľné)')}`}
          // Legacy explains the e-mail rather than just asking for it, and only while the programme is
          // on offer — an address collected for nothing needs no justification.
          hint={joinLoyaltyOffered ? tt('Email je potrebný pre vernostný program. Môžete ho zadať aj bez registrácie do programu.') : undefined}
        >
          <input
            id="field-email"
            data-field="email"
            type="email"
            autoComplete="email"
            value={customerEmail}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="vas@email.sk"
            className={FIELD_CLASS}
          />
        </CheckoutField>

        {/* §9.163, the owner's point 46 - *"zapojte sa do vernostného programu should be under the
          * E-mail"*. It was a card of its own further down the page, which put the tick a scroll away
          * from the address it depends on: the field's own hint already says *"Email je potrebný pre
          * vernostný program"*, and the thing that sentence refers to was nowhere near it. */}
        {/* §9.163 - legacy's panel without the name it prints. A prompt, not a gate: ordering as a
          * guest is still allowed, but the points this address is owed need a sign-in, and being told
          * that before the order beats discovering it after. */}
        {/* §9.218 - the third state, and legacy's card: signed in and in the programme. The two
          * below it are for a guest, so all three are mutually exclusive by construction — nobody
          * signed in is offered a tick to join what they are already in. */}
        {user !== null && rates !== null && rates.isActive && (
          <div
            className="bg-primary/8 border border-primary/25 rounded-2xl px-4 py-3 space-y-3"
            data-loyalty-greeting
          >
            <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
              ★
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold truncate">
                <T>Vítajte späť</T>
                {knownName === '' ? '' : `, ${knownName.split(' ')[0] ?? ''}`}
              </span>
              <span className="block text-xs text-muted-foreground">
                {memberLevel === '' ? (
                  <T>Vernostný člen</T>
                ) : (
                  <>
                    <T>Vernostný člen</T>
                    {' · '}
                    {/* §9.250 - named, from `tierFor`, which is the reader `ProfilePage` uses. The
                      * comment here used to refuse a name because the thresholds carry none and two
                      * screens inventing their own would disagree; §9.246 gave both the franchise's
                      * own `name`, `emoji` and `color`, so sharing the reader is what keeps them
                      * agreeing. */}
                    {/* §9.279, owner O27 - *"for naming use FARBA from loyalty… these emojis set, and
                      * also názov… in profile, and also in checkout."* The profile has styled this
                      * badge with the tier's own colour since §9.246 (`ProfilePage.tsx:775`) and the
                      * checkout printed it in the surrounding text colour, so one screen showed the
                      * franchise's palette and the other did not. Same three values, same treatment. */}
                    <span
                      className={memberTier === null ? undefined : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border'}
                      style={
                        memberTier === null
                          ? undefined
                          : {
                              background: `${memberTier.color}1A`,
                              color: memberTier.color,
                              borderColor: `${memberTier.color}55`,
                            }
                      }
                      data-loyalty-level={memberLevel}
                      data-loyalty-tier-name={memberTier?.name ?? memberLevel}
                      data-loyalty-tier-colour={memberTier?.color ?? ''}
                    >
                      {memberTier === null ? memberLevel : `${memberTier.emoji} ${memberTier.name}`}
                    </span>
                  </>
                )}
              </span>
            </span>
            <span className="text-right shrink-0">
              <span className="block text-[11px] text-muted-foreground">
                <T>Zostatok bodov</T>
              </span>
              {/* Said even at nought: the redemption control below is right to stay quiet when there
                * is nothing to spend, but *you have 0* is the fact somebody opens this looking for. */}
              <span className="block text-sm font-bold text-primary" data-loyalty-balance={balance}>
                {formatCount(balance)} <T>b.</T>
              </span>
            </span>
            </div>

            {/* §9.278, owner O23 - what this order earns, said beside the tier that decides it and
              * only above the ordinary rate. It belongs here rather than in the redemption panel:
              * this is about what the order *earns*, not what it spends. */}
            {multiplierNote(memberTier?.name ?? null, memberTier?.multiplier ?? 1) !== '' && (
              <p className="text-[11px] text-primary font-semibold pt-1" data-points-multiplier>
                {multiplierNote(memberTier?.name ?? null, memberTier?.multiplier ?? 1)}
              </p>
            )}

            {/* §9.250 - the owner's N9. Legacy keeps the redemption inside this same box
              * (`Checkout.tsx:1556`–`:1780`); AGOS had it two cards further down, behind the address
              * form, under a second *Vernostné body* heading. Moved, not rebuilt: same slider, same
              * hooks, same arithmetic. */}
            {balance > 0 && (
              <div className="border-t border-primary/20 pt-3 space-y-2" data-redeem>
                {!identityHolds ? (
                  <p className="text-xs text-muted-foreground" data-redeem-note data-redeem-identity>
                    <T>Vernostné body a trvalá zľava platia len s e-mailom vášho účtu.</T>
                  </p>
                ) : redeemNoteFor(pointsBlocked) !== '' ? (
                  <p className="text-xs text-muted-foreground" data-redeem-note>
                    {redeemNoteFor(pointsBlocked)}
                  </p>
                ) : (
                  <>
                    {/* §9.278, owner O23 - legacy's panel. The heading and the number box are here
                      * from the moment the card opens; the owner's complaint was that *Koľko bodov
                      * uplatniť?* only appeared after pressing a button. */}
                    <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                      <T>Koľko bodov uplatniť?</T>
                    </p>

                    {/* Legacy's two tiles: what the basket is, and what it becomes. */}
                    <div className="grid grid-cols-2 gap-2 text-xs" data-redeem-preview>
                      <div className="rounded-lg bg-background border border-border px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                          <T>Suma košíka</T>
                        </p>
                        <p className="font-bold tabular-nums">{formatCents(absorbableCents)}</p>
                      </div>
                      <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-bold">
                          <T>Po uplatnení</T>
                        </p>
                        <p className="font-bold tabular-nums text-emerald-700 dark:text-emerald-400" data-redeem-after>
                          {formatCents(Math.max(0, absorbableCents - redeemedCents))}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="number"
                        data-redeem-amount
                        aria-label={tt('Koľko bodov uplatniť?')}
                        min={0}
                        max={redeemable}
                        step={1}
                        value={Math.min(redeem, redeemable)}
                        onChange={(event) => setRedeem(Number(event.target.value) || 0)}
                        className="w-24 px-2 py-1.5 rounded-lg border border-border bg-background text-right text-sm font-bold tabular-nums"
                      />
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        <T>z</T> {formatCount(balance)} <T>bodov</T>
                      </span>
                      <div className="flex-1" />
                      <span className="text-sm font-bold whitespace-nowrap" data-redeem-value>
                        −{formatCents(redeemedCents)}
                      </span>
                    </div>

                    <input
                      type="range"
                      data-redeem-slider
                      aria-label={tt('Uplatniť vernostné body')}
                      min={0}
                      max={redeemable}
                      step={1}
                      value={Math.min(redeem, redeemable)}
                      onChange={(event) => setRedeem(Number(event.target.value))}
                      className="w-full accent-primary"
                    />

                    {/* The six legacy offers, with the ones the ceiling cannot reach dropped. */}
                    <div className="flex items-center gap-1.5 flex-wrap" data-redeem-quick>
                      {quickAmounts(rates, redeemable).map((amount: QuickAmount) => (
                        <button
                          key={amount.points}
                          type="button"
                          data-redeem-quick-amount={amount.points}
                          onClick={() => setRedeem(amount.points)}
                          className="px-2 py-1 rounded-md bg-muted hover:bg-muted/70 text-[11px] font-bold text-muted-foreground"
                        >
                          {amount.label}
                        </button>
                      ))}
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                      {/* The balance and what it is worth are different facts, and legacy shows
                        * both. */}
                      <T>Zostatok</T>: {balanceNote(rates, balance)}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {existingAccount && joinLoyaltyOffered && customerEmail.includes('@') && (
          <div
            className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl px-4 py-3"
            data-account-exists
          >
            <User className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-bold">
                <T>Účet pre tento e-mail existuje</T>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                <T>
                  Pre vernostné body, históriu objednávok a osobnú zľavu sa najprv prihláste.
                </T>
              </p>
            </div>
          </div>
        )}

        {joinLoyaltyOffered && !(existingAccount && customerEmail.includes('@')) && (
          <label className="flex items-start gap-3 cursor-pointer -mt-1">
            <input
              type="checkbox"
              data-join-loyalty
              checked={joinLoyalty}
              onChange={(event) => setJoinLoyalty(event.target.checked)}
              className="w-5 h-5 rounded accent-primary mt-0.5 shrink-0"
            />
            <span>
              <span className="block text-sm font-bold">
                <T>Zapojte sa do vernostného programu</T>
              </span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                <T>Za objednávky získavate body, ktoré si môžete uplatniť pri ďalšej objednávke.</T>
              </span>
              {/* §9.775, BACKEND.md §10A.429, owner O136 - *"Ako som sa zaregistroval keď to nepýtalo heslo?"*.
                * The tick asks for no password, so it says what follows: a welcome with the link to set one. */}
              {joinLoyalty && (
                <span className="block text-xs text-muted-foreground mt-1" data-join-loyalty-next>
                  <T>Pošleme vám e-mail, cez ktorý si nastavíte heslo - body potom uplatníte po prihlásení.</T>
                </span>
              )}
            </span>
          </label>
        )}

        {needsAddress && saved.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-saved-addresses>
            {saved.map((entry) => (
              <button
                key={entry.address}
                type="button"
                data-saved-address={entry.address}
                aria-pressed={customerAddress === entry.address}
                onClick={() => {
                  // §9.52 — chosen means resolved. This set the text and nothing else, so the one
                  // customer whose address the restaurant already knows was the one who could not be
                  // quoted a fee. A failed lookup erases nothing - but since §9.706 the earlier point is not
                  // *carried* for this text either, because a quote that belongs to another address was never a
                  // good quote for this one (FB-1010). A pin or a located point is forgotten here: choosing an
                  // address is choosing a place.
                  setAddress(entry.address)
                  setHeldPoint(heldWhenAnAddressIsChosen)
                  void geocodeOnce(entry.address).then((place) => {
                    if (place !== null) {
                      setHeldPoint(foundForAddress({ lat: place.lat, lng: place.lon }, entry.address))
                    }
                  })
                }}
                className={`min-h-[36px] px-3 rounded-xl border text-xs font-semibold transition-colors ${
                  customerAddress === entry.address
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                {/* §9.332 - the owner: *"add dropdown for these addresses, so I can actually see
                    that Address."* This read `entry.label ?? entry.address`, so an address the
                    customer had named showed **only the name**: *Domov*, with the street nowhere on
                    the screen. Both now, label first - the label is how they recognise it and the
                    address is what they are checking before they order to it. */}
                {addressSummary(entry)}
                {entry.isDefault && <span className="ml-1 opacity-60">★</span>}
              </button>
            ))}
          </div>
        )}
        {needsAddress && (
          <CheckoutField
            id="field-address"
            icon={MapPin}
            label={tt('Adresa doručenia *')}
            hint={tt('Funguje aj pre adresy, ktoré Google nepozná')}
          >
            {/* The field said *Začnite písať adresu…* and *funguje aj pre adresy, ktoré Google
                nepozná* while offering no suggestions at all - the screen was built expecting an
                autocomplete that was parked on a missing Maps key. The key arrived 2026-08-03
                (BLOCKED #7); this connects what the copy already promised.
                A delivery address a customer types is where a wrong one costs a delivery, so this
                is the highest-volume of the three surfaces the component now serves.
                `id` and `data-field` are preserved because five e2e assertions find the field by
                them, and the native `street-address` autofill stays: a browser that already knows
                the customer's address should still offer it, ahead of anything Google says. */}
            <AddressField
              value={customerAddress}
              // §9.706, FB-1010 - typing sets the text and nothing else, and that is now enough: the point
              // derived from `heldPoint` stops being carried once the text is no longer the one it was found for.
              onChange={(text) => {
                fieldText.current = text
                setAddress(text)
              }}
              // §9.51 — the point the field already resolved. Without this the checkout threw it
              // away and `point` was only ever set by *use my location*, so every typed address was
              // refused however central it was. `lon` is Google's spelling; `zoneFor` takes `lng`,
              // and reading the wrong one is `undefined` → `NaN` → outside every radius.
              // §9.706: found for the text the field now shows - the formatted address, or the
              // suggestion's own name when the provider gave none.
              onResolved={(place) =>
                setHeldPoint(
                  foundForAddress(
                    { lat: place.lat, lng: place.lon },
                    place.formattedAddress !== '' ? place.formattedAddress : fieldText.current,
                  ),
                )
              }
              // §9.372: an unresolved pick must not leave the previous address's coordinates
              // behind the new text - the fee and the zone are both read off `point`.
              onUnresolved={() => setHeldPoint(null)}
              ariaLabel={tt('Adresa doručenia')}
              placeholder={tt('Začnite písať adresu...')}
              inputProps={
                {
                  id: 'field-address',
                  'data-field': 'address',
                  autoComplete: 'street-address',
                  className: FIELD_CLASS,
                } as InputHTMLAttributes<HTMLInputElement>
              }
              trailing={
                <button
                  type="button"
                  data-locate
                  onClick={locate}
                  disabled={isLocating}
                  aria-label={tt('Použiť moju polohu')}
                  title={tt('Funguje aj pre adresy, ktoré Google nepozná')}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors"
                >
                  {/* §9.68's accessible name survives the move - an icon-only button is only as
                    * good as what it announces. The title is legacy's, and it is the sentence that
                    * explains why anyone would press this instead of typing. */}
                  {isLocating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <LocateFixed className="w-4 h-4" />
                  )}
                </button>
              }
            />
          </CheckoutField>
        )}
        {/* §9.705, owner O81 - *"That 'Mapa' should be also shown in Checkout for customer."* The order builder's
            map (§9.694), under the address it draws. Collapsed until opened and Leaflet loaded only then, so the
            first paint and every pickup order are unchanged. A drop is the customer's own place (§9.706): the
            zone, the fee and the minimum follow from `point` below exactly as for a picked address, and the
            coordinates sent with the order are the pin's. */}
        {needsAddress && (
          <CheckoutPinMap
            point={point}
            depot={depotPoint}
            onPinMoved={(dropped) => setHeldPoint(givenAsAPlace(dropped))}
            onPinAddress={setAddress}
            zones={pinMapZones}
          />
        )}
        <CheckoutField id="field-notes" icon={StickyNote} label={tt('Poznámka k objednávke')}>
          <textarea
            id="field-notes"
            data-field="notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={tt('Napr. zvonček nefunguje, volať...')}
            className={`${FIELD_CLASS} min-h-[80px]`}
          />
        </CheckoutField>
      </div>

      {/* Legacy has no separate delivery card: the address, the locate button and what the zone
          charges all sit in *Vaše údaje*, because they are one answer to one question. */}
      {/* §9.68 - one row, not two. The locate control shrank to an icon so it could sit beside what
          it produces: a full-width *Použiť moju polohu* took a row of its own directly above the
          line naming the zone it resolves, which is the pair the owner was reading. */}
      {/* §9.164 - the button moved into the address box, where the owner asked for it. What is left
          here is the zone line, which keeps `data-delivery`. */}
      {needsAddress && (
        <div className="flex items-center gap-2" data-delivery>
            {delivery.kind === 'ok' ? (
              // §9.67 — the zone, the fee, and the minimum. The minimum is the one number here
              // that can stop an order, and it was the one this line did not say.
              <p className="text-xs" data-delivery-zone={delivery.zone.name}>
                {zoneLine(delivery.zone, delivery.feeCents)}
              </p>
            ) : (
              // §9.51 — nothing at all until there is something to say. `unknown` has no note, and an
              // empty amber paragraph is still a paragraph: it reserves space and reads as a warning
              // that failed to load.
              deliveryNote(delivery) !== '' && (
                <p className="text-xs text-amber-500" data-delivery-note={delivery.kind}>
                  {deliveryNote(delivery)}
                  {/* FB-830, §9.545 - the retry is what turns a possible late refusal into an avoidable one, so it
                      belongs on the sentence rather than somewhere the customer has to look for it. */}
                  {delivery.kind === 'unreadable' && (
                    <button
                      type="button"
                      onClick={() => void (couriersDeliver ? pointQuote.refetch() : refetchZones())}
                      data-delivery-retry
                      className="ml-1.5 underline font-semibold"
                    >
                      <T>Skúsiť znova</T>
                    </button>
                  )}
                </p>
              )
            )}
            {/* §9.64 - what the basket is still short of, and only when there is something to
              * reach. Below the zone's minimum comes first: that is a gate, not a saving, and no
              * tier matters while the order cannot be placed at all. */}
            {(() => {
              const progress = basketProgress(delivery, subtotal)
              if (progress === null) return null
              return (
                <div className="space-y-1" data-basket-progress={progress.kind}>
                  <p className="text-[11px] text-muted-foreground">
                    {progress.kind === 'minimum' ? (
                      <>
                        <T>Do minimálnej objednávky chýba</T> {formatCents(progress.missingCents)}
                      </>
                    ) : progress.free ? (
                      <>
                        <T>Do dopravy zadarmo chýba</T> {formatCents(progress.missingCents)}
                      </>
                    ) : (
                      <>
                        <T>Ešte</T> {formatCents(progress.missingCents)}{' '}
                        <T>a doprava bude</T> {formatCents(progress.feeCents)}
                      </>
                    )}
                  </p>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{ width: `${Math.round(progressFraction(progress) * 100)}%` }}
                    />
                  </div>
                </div>
              )
            })()}
        </div>
      )}
      </section>



      {/* Joining is offered to a guest, once, at the moment they are already typing their details.
          Not offered to somebody signed in - they are in it. */}
      <section className="bg-card rounded-3xl p-6 border border-border shadow-sm space-y-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm">3</span>
          <T>Spôsob platby</T>
        </h2>
      {/* §9.766 - up to four tiles since QR platba: two columns when there are four, so none sits alone on a row. */}
      <div className={`grid grid-cols-1 ${methods.length > 3 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4`} data-payment>
        {methods.map((method) => {
          const chosen = paymentMethod === method
          // Legacy tints the online option violet rather than primary — it is the one that leaves the
          // site for a bank, and it looks different because it behaves differently.
          const online = method === 'card_online'
          const frame = chosen
            ? online
              ? 'border-violet-500 bg-violet-50 text-violet-700'
              : 'border-primary bg-primary/5 text-primary'
            : online
              ? 'border-border hover:border-violet-300 text-foreground'
              : 'border-border hover:border-primary/30 text-foreground'
          const tile = chosen
            ? online
              ? 'bg-violet-500 text-white'
              : 'bg-primary text-white'
            : 'bg-secondary text-muted-foreground'
          return (
            <button
              key={method}
              type="button"
              data-payment-option={method}
              aria-pressed={chosen}
              onClick={() => setChosenPayment(method)}
              className={`flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${frame}`}
            >
              <div className={`p-2 rounded-xl shrink-0 ${tile}`}>
                {method === 'cash' ? (
                  <Banknote className="w-5 h-5" />
                ) : method === 'qr_pay' ? (
                  <QrCode className="w-5 h-5" />
                ) : (
                  <CreditCard className="w-5 h-5" />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-bold">{paymentMethodLabel(method)}</div>
                <div className="text-xs opacity-70">{paymentMethodNote(method)}</div>
              </div>
            </button>
          )
        })}
      </div>
      {/* §9.479 - where the tile they pressed used to be. A notice rather than a refusal: blocking the
          submit would strand a ready basket behind a settings call that may not come back, and cash is a
          method the restaurant can always honour. */}
      {substitution !== null && (
        <p
          className="mt-3 text-xs rounded-xl border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2"
          data-payment-substituted
        >
          {/* No `<T>` wrapper: the sentence is already built from `tt()` pieces, which is how
              `paymentMethodNote` renders two lines above. */}
          {/* §9.501 - the same box, a different sentence, and a way out. Asserting *card is unavailable*
              when the read simply failed is a guess, and the customer's only recourse was to pay another
              way or ring up. */}
          {paymentsUnknown ? paymentUnknownNote(paymentMethod) : paymentSubstitutionNote(substitution)}
          {paymentsUnknown && (
            <button
              type="button"
              onClick={() => void refetchPayments()}
              data-payment-retry
              className="ml-2 underline font-semibold"
            >
              <T>Skúsiť znova</T>
            </button>
          )}
        </p>
      )}
      </section>
      </div>

      {/* The summary column, which legacy keeps in view while the form is filled. */}
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-24 space-y-4">
      <div className="bg-card rounded-3xl p-6 border border-border shadow-sm space-y-5" data-summary>
        {/* What is being bought, which this card did not say at all - FB-C53. Legacy heads the summary
            with the order and lets it be changed here, because the checkout is the last place anybody
            reads it back before paying, and going to the menu to fix a quantity loses the form. */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-display font-bold">
            <T>Vaša objednávka</T>
          </h2>
          <button
            type="button"
            data-clear-cart
            onClick={clear}
            className="text-xs text-destructive hover:text-destructive/80 font-medium transition-colors"
          >
            <T>Vyprázdniť košík</T>
          </button>
        </div>

        <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-2">
          {/* §9.166 - a box is one entry. Its lines have carried `boxGroupId` since the box was
              built; this screen was the one place that never read it, so a box of three arrived as
              three rows with three steppers, any of which could take it apart. */}
          {groupBasket(priced).map((group) =>
            group.kind === 'box' ? (
              <div
                key={group.groupId}
                data-checkout-box={group.groupId}
                className="border border-amber-200 rounded-2xl overflow-hidden bg-amber-50/50"
              >
                <div className="flex items-center justify-between px-3 py-2 bg-amber-100/70 border-b border-amber-200">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Package className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    <span className="text-xs font-black text-amber-700 uppercase tracking-wide truncate">
                      {group.name}
                    </span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-bold text-amber-700" data-box-total>
                      {formatCents(group.totalCents)}
                    </span>
                    {/* One bin for the whole box, and no stepper: half a box is not something the
                        kitchen makes. `removeBoxGroup` is what the cart wrote for this. */}
                    <button
                      type="button"
                      data-box-remove
                      aria-label={tt('Odobrať z košíka')}
                      onClick={() => removeBoxGroup(group.groupId)}
                      className="w-6 h-6 rounded-full bg-white text-destructive flex items-center justify-center shadow-sm hover:bg-destructive hover:text-white transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                </div>
                <div className="divide-y divide-amber-100 px-3">
                  {group.items.map((item) => (
                    <div key={item.key} className="flex items-center gap-2 py-1.5" data-box-item>
                      {item.line.boxSlotLabel !== undefined && (
                        <span className="text-xs font-bold text-amber-500 uppercase w-16 shrink-0 truncate">
                          {item.line.boxSlotLabel}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-foreground flex-1 min-w-0 truncate">
                        {item.name}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatCents(item.unitCents)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : group.addons.length === 0 ? (
              <BasketLine
                key={group.entry.key}
                entry={group.entry}
                onRemove={removeLine}
                onChangeBy={(_key, delta) => stepLine(group.entry, delta)}
              />
            ) : (
              /* §9.713 - what was ordered with a product is drawn under it, as the box's slots are, so the fries read
                 as burger Bacon's. Each keeps its own stepper: the customer may still take the dip off and keep the
                 burger, and removing the burger takes its add-ons with it (`removeLine`). */
              <div key={group.entry.key} className="space-y-2" data-checkout-line-family>
                <BasketLine entry={group.entry} onRemove={removeLine} onChangeBy={changeBy} />
                <div className="ml-4 pl-3 border-l-2 border-primary/20 space-y-2" data-checkout-addons>
                  {group.addons.map((addon) => (
                    <BasketLine
                      key={addon.key}
                      entry={addon}
                      onRemove={removeLine}
                      onChangeBy={changeBy}
                      addon
                    />
                  ))}
                </div>
              </div>
            ),
          )}

          {/* §9.173 / §9.185 - the day's menu, in a box like the burger box, and removed as one.
              It is listed and totalled but not stepped: a soup's price changes when the main beside
              it leaves, which is `repriceLunchSoups`. That is also the owner's reason for one bin
              rather than a bin per meal - taking half a set out silently reprices the other half. */}
          {lunchBasket.length > 0 && (
            <div
              className="border border-amber-200 rounded-2xl overflow-hidden bg-amber-50/50"
              data-checkout-lunch-box
            >
              <div className="flex items-center justify-between px-3 py-2 bg-amber-100/70 border-b border-amber-200">
                <span className="flex items-center gap-1.5 min-w-0">
                  <Soup className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span className="text-xs font-black text-amber-700 uppercase tracking-wide truncate">
                    <T>Denné menu</T>
                  </span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-bold text-amber-700" data-lunch-box-total>
                    {formatCents(lunchCents)}
                  </span>
                  <button
                    type="button"
                    data-lunch-remove
                    aria-label={tt('Odobrať denné menu')}
                    onClick={() => {
                      for (const meal of lunchBasket) removeLine(lineKey(lunchLineOf(meal.meal, meal.date)))
                    }}
                    className="w-6 h-6 rounded-full bg-white text-destructive flex items-center justify-center shadow-sm hover:bg-destructive hover:text-white transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              </div>
              <div className="divide-y divide-amber-100 px-3">
                {lunchBasket.map((meal, index) => (
                  <div
                    key={`lunch-${index}`}
                    className="flex items-center gap-2 py-1.5"
                    data-checkout-lunch-line
                  >
                    <span className="text-sm font-semibold text-foreground flex-1 min-w-0 truncate">
                      {meal.quantity > 1 && `${formatCount(meal.quantity)}× `}
                      {meal.meal.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {composedLines.map((line) => (
            <div
              key={lineKey(line)}
              className="flex gap-4 items-center"
              data-checkout-composed-line={line.quantity}
            >
              <div className="flex-1 min-w-0">
                {/* §9.245 - §9.172's count, on the list it never reached. Five gifts read as one
                    thing, and a customer reads their order back off the list. */}
                <div className="font-bold">
                  {line.quantity > 1 && `${formatCount(line.quantity)}× `}
                  {line.composed?.name}
                </div>
                {line.giftRuleId !== undefined && (
                  <div
                    className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400"
                    data-line-gift={line.giftRuleId}
                  >
                    🎁 <T>Darček</T>
                  </div>
                )}
                <div className="text-primary font-bold text-sm">
                  {formatCents((line.composed?.unitPriceCents ?? 0) * line.quantity)}
                </div>
              </div>
              <LineStepper
                quantity={line.quantity}
                isGift={line.giftRuleId !== undefined}
                onRemove={() => removeLine(lineKey(line))}
                onChangeBy={(delta) => changeBy(lineKey(line), delta)}
              />
            </div>
          ))}

          {/* §9.142's reasoning, §9.167's placement. Legacy evaluates the gift rule on its checkout
              as well as its menu, and a customer who crossed the threshold and came straight here
              would otherwise never be told. Re-evaluated rather than remembered: this is what the
              basket earns *now*, which is also right after a line was removed on the way. A card in
              the order and not a dialog - a popup over somebody entering an address is an
              interruption, and leaving it alone is the refusal.

              In the order list rather than among the totals, because a gift is a thing you are
              getting and not a subtraction from what you owe. */}
          {/* FB-831, §9.546 - **said, not silently absent.** A customer cannot ask for an offer they never saw, so a
              failed rule read says the gift could not be checked and offers a retry. **Not a fabricated offer**:
              adding a gift line from a read that failed would send `giftRuleId` for a rule nobody read, and §9.524's
              own exclusion exists because a composed line's synthetic id decodes to a plausible rule id. */}
          {giftsFailed && (
            <div
              className="mt-2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/30"
              data-gift-unreadable
            >
              <p className="text-xs text-amber-700 dark:text-amber-300">
                <T>Nepodarilo sa zistiť, či na túto objednávku patrí darček.</T>{' '}
                <button
                  type="button"
                  onClick={() => void refetchGifts()}
                  data-gift-retry
                  className="underline font-semibold"
                >
                  <T>Skúsiť znova</T>
                </button>
              </p>
            </div>
          )}
          {/* FB-831, §9.546 - one sentence for both campaign reads, because from the customer's side it is one fact:
              a discount may apply that we could not read. The server computes both (`WebOrderDiscounts`), so it says
              the total may come out lower rather than promising a figure. */}
          {promoUnreadable && (
            <p className="mt-2 text-xs text-muted-foreground" data-promo-unreadable>
              <T>
                Akcie a happy hours sa teraz nepodarilo načítať. Ak nejaká platí, server ju odpočíta - suma nižšie
                môže byť vyššia, než nakoniec zaplatíte.
              </T>
            </p>
          )}
          {[...checkoutGifts, ...amountGifts].map((offer) => (
            <div
              key={`${offer.ruleType ?? 'product'}:${offer.ruleId}`}
              data-checkout-gift={offer.ruleId}
              data-checkout-gift-kind={offer.ruleType ?? 'product'}
              className="mt-2 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/50 dark:bg-red-950/30"
            >
              <span className="text-2xl shrink-0" aria-hidden="true">
                🎁
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-red-600 dark:text-red-400">
                  <T>Darček k objednávke</T>
                </p>
                <p className="text-sm font-semibold text-red-800 dark:text-red-200 truncate">
                  {/* The count and the price, because legacy shows both. *2× Coca-Cola* is a
                      different offer from *Coca-Cola*, and a price that is not zero is what says
                      the gift is discounted rather than free. */}
                  {offer.count > 1 && `${formatCount(offer.count)}× `}
                  {offer.giftName}
                  {offer.giftPriceCents > 0 && ` – ${formatCents(offer.giftPriceCents)}`}
                </p>
              </div>
              <button
                type="button"
                data-checkout-gift-accept
                onClick={() => add(giftLine(offer))}
                className="shrink-0 rounded-xl bg-red-600 hover:bg-red-700 active:scale-95 px-4 py-2 text-xs font-bold text-white transition-all"
              >
                <T>Pridať darček</T>
              </button>
            </div>
          ))}
        </div>


        <div className="pt-5 border-t border-border border-dashed space-y-4">
          {/* Legacy keeps the discount code beside the total it changes rather than in a numbered
              section down the page - a coupon is read against a price. Its referral field is the same
              box in legacy; kept separate here because the two endpoints validate differently. */}
            {exclusive ? (
              <p className="text-xs text-muted-foreground" data-coupon-replaced>
                {replacedNote()}
              </p>
            ) : (
              <>
                {/* §9.71 - one box, both codes tried. Legacy has a single field; the two AGOS had
                  * were the difference the owner reported. `applyCode` tries the coupon endpoint and
                  * then the referral one, so a refusal from both can say both were tried — which is
                  * the sentence legacy's single box cannot produce. */}
                <div className="flex gap-2">
                  <input
                    data-coupon-input
                    value={codeInput}
                    onChange={(event) => setCodeInput(event.target.value)}
                    placeholder={tt('Zľavový alebo odporúčací kód')}
                    className="flex-1 min-h-[40px] rounded-xl border border-border bg-card px-3 text-sm"
                  />
                  <button
                    type="button"
                    data-coupon-apply
                    onClick={() => void applyCode()}
                    disabled={
                      codeInput.trim() === '' ||
                      validateCoupon.isPending ||
                      validateReferral.isPending
                    }
                    className="min-h-[40px] px-3 rounded-xl border border-border text-xs font-semibold disabled:opacity-50"
                  >
                    <T>Uplatniť</T>
                  </button>
                </div>
                {codeError !== '' && (
                  <>
                    <p className="text-xs text-red-500" data-code-error>
                      {codeError}
                    </p>
                    {/* The specific refusal, kept. "Both were tried" is the merge; *why* each said
                      * no is the thing the split was protecting, and an expired coupon is a real
                      * coupon — telling that customer only that their code is neither would be a
                      * worse answer than the two boxes gave. */}
                    {couponError !== '' && (
                      <p className="text-[11px] text-muted-foreground" data-coupon-error>
                        {couponError}
                      </p>
                    )}
                    {referralProblem !== '' && referralProblem !== couponError && (
                      <p className="text-[11px] text-muted-foreground" data-referral-problem>
                        {referralProblem}
                      </p>
                    )}
                  </>
                )}
              </>
            )}
            {liveCoupon !== null && (
              <p className="text-xs text-emerald-500" data-coupon-applied={liveCoupon.code}>
                {liveCoupon.code} - {liveCoupon.label}
              </p>
            )}
            {/* §9.782 - an organization's code says where the other share goes. */}
            {liveCoupon !== null && organizationNote(liveCoupon) !== '' && (
              <p className="text-xs text-emerald-600" data-coupon-organization>
                {organizationNote(liveCoupon)}
              </p>
            )}
            {referral !== null && (
              <p className="text-xs text-emerald-600" data-referral-applied={referral}>
                {referral} - <T>uplatnené</T>
              </p>
            )}

                    {/* §9.18 - the control legacy draws, not a bare checkbox.
              A full-width toggle card, the same shape as the payment and delivery choices above, so
              the three read as one set. The second line reads the chosen slot back: a customer who
              set a time three screens ago needs to see it without reopening the pickers, and that
              read-back is the part whose absence is felt more than the border. */}
            <button
              type="button"
              data-preorder-toggle
              aria-pressed={preorder !== null}
              /* §9.709 - a future day's lunch makes the časenka mandatory, so the switch cannot be
                 turned off; the second line below says why rather than leaving a dead control. */
              disabled={lunchLocked}
              data-preorder-locked={lunchLocked ? lunchDay : undefined}
              onClick={() => {
                if (lunchLocked) return
                setPreorder(preorder === null ? { date: '', time: '' } : null)
              }}
              className={cn(
                'w-full flex items-center gap-4 rounded-2xl border-2 p-4 transition-all text-left',
                preorder !== null
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/30',
                lunchLocked && 'cursor-not-allowed',
              )}
            >
              <div
                className={cn(
                  'p-2.5 rounded-xl shrink-0 transition-colors',
                  preorder !== null ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground',
                )}
              >
                <CalendarClock className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">
                  <T>Objednať na neskôr / Časenka</T>
                </div>
                <div className="text-xs text-muted-foreground" data-preorder-summary>
                  {scheduledSummary === null ? (
                    <T>Zvoľte termín doručenia vopred</T>
                  ) : (
                    scheduledSummary
                  )}
                </div>
                {/* §9.709 - legacy's own line under a switch that cannot be turned off. */}
                {lunchLocked && (
                  <div className="text-xs font-semibold text-primary" data-preorder-required>
                    <T>Povinné z dôvodu obedového menu na</T> {lunchDayText(lunchDay)}
                  </div>
                )}
              </div>
              <div
                className={cn(
                  'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                  preorder !== null ? 'bg-primary border-primary' : 'border-border',
                )}
              >
                {preorder !== null && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
              </div>
            </button>
            {preorder !== null && (
              <div className="flex gap-2">
                {/* §9.219, made a lock by §9.709 - pinned to the day the basket's daily menu is
                  * for, and bounded by the hours that menu is served: a Časenka for 21:00 on a menu
                  * served 11:00–14:00 is an order the kitchen will not honour. Disabled rather than
                  * read-only while a future day's lunch is in the basket, as legacy's is; unconstrained
                  * for any other basket. */}
                <input
                  type="date"
                  data-preorder-date
                  value={preorder.date}
                  min={lunchDay ?? undefined}
                  max={lunchDay ?? undefined}
                  disabled={lunchLocked}
                  onChange={(event) => {
                    if (lunchLocked) return
                    setPreorder({ ...preorder, date: event.target.value })
                  }}
                  className="flex-1 min-h-[40px] rounded-xl border border-border bg-card px-3 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                />
                <input
                  type="time"
                  data-preorder-time
                  value={preorder.time}
                  min={lunchLocked ? lunchWindow?.from : undefined}
                  max={lunchLocked ? lunchWindow?.to : undefined}
                  onChange={(event) => setPreorder({ ...preorder, time: event.target.value })}
                  className="flex-1 min-h-[40px] rounded-xl border border-border bg-card px-3 text-sm"
                />
              </div>
            )}
            {/* §9.709 - the serving window's quarter-hours, legacy's quick-pick chips. The time is
              * required and starts empty, so these are the one-tap way to choose it. */}
            {lunchLocked && preorder !== null && lunchSlotOptions.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-lunch-slots>
                {lunchSlotOptions.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    data-lunch-slot={slot}
                    aria-pressed={preorder.time === slot}
                    onClick={() => setPreorder({ ...preorder, time: slot })}
                    className={cn(
                      'px-3 py-2 rounded-xl border text-sm font-bold min-w-[64px] transition-all',
                      preorder.time === slot
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:border-primary/40',
                    )}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            )}
            {/* Said out loud, because a picker that refuses a date without explaining reads as
              * broken — and this one is pinned by something the customer chose two screens ago. */}
            {lunchDay !== null && (
              <p className="text-xs text-muted-foreground" data-lunch-preorder={lunchDay}>
                <T>Obedové menu je na tento deň - časenka je preto povinná a termín pevný</T>
                {lunchWindow === null ? '' : `, ${lunchWindow.from}–${lunchWindow.to}`}.
              </p>
            )}
            {scheduling !== '' && (
              <p className="text-xs text-amber-500" data-preorder-problem>
                {scheduling}
              </p>
            )}
        </div>

      <dl className="text-sm space-y-1 pt-5 border-t border-border border-dashed">
        <div className="flex justify-between">
          <dt>
            <T>Medzisúčet</T>
          </dt>
          <dd data-subtotal>{formatCents(subtotal)}</dd>
        </div>
        {needsAddress && (
          <div className="flex justify-between">
            <dt>
              <T>Doručenie</T>
            </dt>
            <dd data-delivery-cost>
              {/* An unknown zone is **not** zero. Printing `0,00 €` beside a real total reads as free
                  delivery, which is precisely FB-188 on the manager side - and the fee here is decided
                  by an address the customer has not given yet. Legacy says so. */}
              {delivery.kind !== 'ok' && !deliveryFree ? (
                <span className="italic text-xs text-muted-foreground" data-delivery-unknown>
                  <T>Závisí od adresy</T>
                </span>
              ) : deliveryFree ? (
                <span className="flex items-center gap-1.5 justify-end">
                  <span className="line-through text-muted-foreground text-xs">
                    {formatCents(rawDeliveryCents)}
                  </span>
                  <span className="text-emerald-600 font-semibold" data-delivery-free>
                    <T>ZADARMO</T>
                  </span>
                </span>
              ) : (
                formatCents(deliveryCents)
              )}
            </dd>
          </div>
        )}
        {promoCents > 0 && (
          <div className="flex justify-between text-sm text-rose-600 font-medium" data-promo>
            <dt>{blackFridayLabel(promo)}</dt>
            <dd data-promo-cents>−{formatCents(promoCents)}</dd>
          </div>
        )}
        {/* §9.254 - legacy's own line, `Checkout.tsx:2562`. A discount that appears only as a smaller
          * number is one the customer does not know they were given, and the point of a happy hour is
          * that people come during it. */}
        {happyCents > 0 && (
          <div className="flex justify-between text-sm text-rose-600 font-medium" data-happy-hours>
            <dt className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {happyHoursLabel(happy)}
            </dt>
            <dd data-happy-hours-cents>−{formatCents(happyCents)}</dd>
          </div>
        )}
        {/* Said, not computed: the multiplier is applied by the server at order creation, and a
          * second arithmetic to keep in step would buy nothing. */}
        {happyHoursPointsNote(happy) !== '' && (
          <p className="text-[11px] text-muted-foreground" data-happy-hours-points>
            {happyHoursPointsNote(happy)}
          </p>
        )}
        {/* §9.65 - the account's own discount, named. It was always applied and always in the
          * total; one unlabelled *Zľava* line told somebody an amount came off and not that their
          * account carries a standing benefit. The two lines always sum to what was taken off,
          * because the stack is capped and a summary that does not add up is worse than one line. */}
        {(() => {
          const split = discountBreakdown(discountUser, subtotal, discount)
          return (
            <>
              {split.personalCents > 0 && (
                <div className="flex justify-between text-emerald-500" data-personal-discount>
                  <dt>{personalDiscountLabel(discountUser)}</dt>
                  <dd data-personal-discount-cents>−{formatCents(split.personalCents)}</dd>
                </div>
              )}
              {split.otherCents > 0 && (
                <div className="flex justify-between text-emerald-500">
                  <dt>
                    <T>Zľava</T>
                  </dt>
                  <dd data-discount>−{formatCents(split.otherCents)}</dd>
                </div>
              )}
            </>
          )
        })()}
        {/* §9.182 - its own row, not folded into the price: a deposit is not what the food costs and
            it comes back. Legacy prints it as its own receipt line for the same reason. */}
        {deposits > 0 && (
          <div className="flex justify-between">
            <dt>
              <T>Zálohy (vratné)</T>
            </dt>
            <dd data-deposits>{formatCents(deposits)}</dd>
          </div>
        )}
        <div className="flex justify-between font-bold pt-1 border-t border-border">
          <dt>
            <T>Spolu na úhradu</T>
          </dt>
          <dd data-total>{formatCents(total)}</dd>
        </div>
        {roundingNote(exact, total) !== '' && (
          <p className="text-[11px] text-muted-foreground" data-rounding-note>
            {roundingNote(exact, total)}
          </p>
        )}
      </dl>

      {/* Legacy states the refusal beside the button, in the summary, at the severity it deserves - a
          bordered destructive panel, not a pale strip at the top of the page - and offers the way
          round it. This port had the sentence but neither the placement nor the advice (FB-C53), and
          left a green *Objednať* looking merely disabled, so the reason had to be hunted for. */}
      {closed && (
        <div
          className="rounded-2xl border-2 border-destructive bg-destructive/10 px-4 py-3 flex items-start gap-3"
          data-open-state={state}
        >
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-black text-destructive text-sm">{openNote(state)}</p>
            <p className="text-xs text-destructive/90 mt-0.5">
              <T>
                Skúste to znova počas otváracích hodín alebo si vytvorte predobjednávku na neskôr
                (Časenka).
              </T>
            </p>
          </div>
        </div>
      )}

      {/* §9.243 - the operator's own notice, under the refusal rather than instead of it: one says
          the state, the other says why. Shown while open too when the owner asked for that. */}
      {announcementShows(announcement, state) && (
        <div
          className="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3"
          data-announcement
        >
          <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
            {announcement.message}
          </p>
        </div>
      )}

      {submitError !== '' && (
        <p className="rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-sm" data-submit-error>
          {submitError}
        </p>
      )}

      {/* §9.417 - the reason, beside the control it explains rather than 600 lines above it.
        *
        * The amber note in the address card and the progress bar under it both stay where they are:
        * they are right for somebody filling in an address, and they are what FB-745 proves is not
        * enough on its own. A customer scrolled to the bottom of a long checkout, pressing a button,
        * is not reading the *Vaše údaje* card - the same placement fault as FB-744's source badge,
        * which sat 100 lines below the order number it qualified.
        *
        * Not rendered when `closed`, because that branch's own label is the sentence. */}
      {!closed && blocked !== null && (
        <p
          className="text-center text-sm font-medium text-amber-600"
          data-submit-block={blocked.code}
        >
          {blocked.message}
        </p>
      )}

      {closed ? (
        <button
          type="button"
          data-place-order
          disabled
          className="w-full min-h-[52px] rounded-2xl bg-muted text-muted-foreground font-bold text-base cursor-not-allowed"
        >
          <T>Reštaurácia je zatvorená</T>
        </button>
      ) : (
        <button
          type="button"
          data-idempotency-key={idempotencyKey}
          data-place-order
          disabled={!maySubmit}
          onClick={() => void submit()}
          className="w-full min-h-[52px] rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold text-base disabled:opacity-50 transition-colors"
        >
          {placeOrder.isPending ? (
            <T>Odosielame…</T>
          ) : paymentMethod === 'card_online' ? (
            <>
              <T>Zaplatiť online</T> {formatCents(total)}
            </>
          ) : (
            <>
              <T>Záväzne objednať za</T> {formatCents(total)}
            </>
          )}
        </button>
      )}

      {/* Legacy's line under the button. A customer is agreeing to terms either way; saying so is the
          difference between a contract and a surprise. */}
      <p className="text-center text-xs text-muted-foreground">
        <T>Odoslaním súhlasíte s obchodnými podmienkami.</T>
      </p>
      </div>
        </div>
      </div>
      </div>
      {/* §9.712 - the menu's question, after the basket's *+*. An offer rather than a step: the unit is in the basket
          whatever the answer, and a customer who closes it is exactly where they were. */}
      {addonPrompt !== null && (
        <AddonPrompt
          key={`${String(addonPrompt.productId)}|${addonPrompt.parentKey}`}
          productName={addonPrompt.productName}
          offer={addonPrompt.offer}
          units={addonPrompt.units}
          onClose={() => setAddonPrompt(null)}
          onConfirm={(chosen) => {
            // §9.712 + §9.713 - each choice as a line of the line whose *+* asked, with its group's terms.
            for (const line of upsellLinesFor(addonPrompt.parentKey, chosen)) add(line)
            setAddonPrompt(null)
          }}
        />
      )}
    </main>
  )
}

/**
 * One ordinary basket row at the checkout — extracted unchanged by §9.166 so the box branch beside it
 * reads as a branch rather than as fifty lines of JSX inside a ternary. Same markup, same attributes,
 * same handlers; only the indentation moved.
 */
/**
 * The row's quantity control — §9.245.
 *
 * **One copy, used by both lists.** It was written inside `BasketLine` and the composed list a
 * hundred lines above never got one, which is FB-405: five gifts with no count and a bin as the only
 * control. A stepper written twice is a stepper that drifts, and this one already failed to reach the
 * second list once.
 *
 * A quantity of one shows the bin rather than a minus, because decrementing to nought and removing
 * are the same intention and two controls for it is one too many. **The plus is hidden for a gift** —
 * a gift is not something to ask for more of, and legacy hides it too.
 */
function LineStepper(props: {
  quantity: number
  isGift: boolean
  onRemove: () => void
  onChangeBy: (delta: number) => void
}) {
  return (
    <div className="flex items-center bg-secondary rounded-full p-1 border border-border/50">
      {props.quantity === 1 ? (
        <button
          type="button"
          data-line-remove
          aria-label={tt('Odobrať z košíka')}
          onClick={props.onRemove}
          className="w-11 h-11 rounded-full bg-white text-destructive flex items-center justify-center shadow-sm hover:bg-destructive hover:text-white active:scale-95 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          type="button"
          data-line-minus
          aria-label={tt('Menej')}
          onClick={() => props.onChangeBy(-1)}
          className="w-11 h-11 rounded-full bg-white text-foreground flex items-center justify-center shadow-sm hover:bg-primary hover:text-white active:scale-95 transition-colors"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
      )}
      <span className="w-8 text-center font-bold text-sm" data-line-quantity>
        {formatCount(props.quantity)}
      </span>
      {!props.isGift && (
        <button
          type="button"
          data-line-plus
          aria-label={tt('Viac')}
          onClick={() => props.onChangeBy(1)}
          className="w-11 h-11 rounded-full bg-white text-foreground flex items-center justify-center shadow-sm hover:bg-primary hover:text-white active:scale-95 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

function BasketLine(props: {
  entry: PricedLine
  onRemove: (key: string) => void
  onChangeBy: (key: string, delta: number) => void
  /** §9.713 - a line ordered with the row above it: drawn smaller, with a plus, inside its parent's family. */
  addon?: boolean
}) {
  const entry = props.entry
  const removeLine = props.onRemove
  const changeBy = props.onChangeBy
  return (
            <div
              key={entry.key}
              className="flex gap-4 items-center"
              data-checkout-line={entry.line.productId}
              {...(props.addon === true ? { 'data-checkout-addon-line': entry.line.productId } : {})}
            >
              <div className="flex-1 min-w-0">
                {/* §9.172 - the count in front of the name, and a gift said to be one. The stepper
                    beside the row carries the quantity, but a customer reading their order back
                    reads the *list*, and five pizzas looked like one. The daily-menu line has
                    printed its count this way since it was written; this one had not. */}
                <div className={props.addon === true ? 'text-sm font-semibold' : 'font-bold'}>
                  {props.addon === true && '+ '}
                  {entry.line.quantity > 1 && `${formatCount(entry.line.quantity)}× `}
                  {entry.name}
                </div>
                {entry.line.giftRuleId !== undefined && (
                  <div
                    className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400"
                    data-line-gift={entry.line.giftRuleId}
                  >
                    🎁 <T>Darček</T>
                  </div>
                )}
                {entry.line.extras !== '' && (
                  <div className="text-xs text-amber-600 font-medium mt-0.5">+{entry.line.extras}</div>
                )}
                <div className="text-primary font-bold text-sm" data-line-total>
                  {formatCents(entry.totalCents)}
                </div>
              </div>
              <LineStepper
                quantity={entry.line.quantity}
                isGift={entry.line.giftRuleId !== undefined}
                onRemove={() => removeLine(entry.key)}
                onChangeBy={(delta) => changeBy(entry.key, delta)}
              />
            </div>
  )
}
